import { z } from 'zod';
/**
 * What changed, version by version, for the players: the welcome screen shows
 * the newest version under the logo and opens this list in a window. Every
 * change a player can see adds a line here (CLAUDE.md, « Changelog »): a new
 * version on top, or a line in the top one while it is not deployed yet.
 *
 * The newest entry is the game's version: nothing else holds that number.
 * Texts are French with their accents. Each line carries its English in `en` (and a title its `titleEn`), shown when the
 * player reads English; the Discord poster keeps the French.
 */
export const CHANGE_KINDS = ['new', 'improved', 'fixed'];
export const ChangelogEntrySchema = z.object({
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    /** Day of the release, ISO (`2026-09-27`). */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    title: z.string().min(1).optional(),
    /** The title in English (ADR 0069); required by the test whenever `title` is set. */
    titleEn: z.string().min(1).optional(),
    /** The title is left out of the apps (ADR 0075), the changes are not. */
    webOnlyTitle: z.boolean().optional(),
    changes: z
        .array(z.object({
        kind: z.enum(CHANGE_KINDS),
        text: z.string().min(1),
        /** The same line in English (ADR 0069); the test requires it on every change. */
        en: z.string().min(1).optional(),
        /**
         * Shown on the web only (ADR 0075): the shop, its prices and purchases
         * have no place in the iOS and Android apps, which sell nothing.
         */
        webOnly: z.boolean().optional(),
    }))
        .min(1),
});
/** Newest first. */
export const CHANGELOG = [
    {
        version: '0.51.1',
        date: '2026-10-17',
        changes: [
            {
                kind: 'improved',
                text: 'Dans la liste des prochains événements de la carte, chaque événement a maintenant son pictogramme et son nom : invasion gobeline, élite géante ou chariot d’or.',
                en: 'In the map’s list of upcoming events, each event now shows its own icon and name: Goblin Invasion, Giant Elite or Gold Cart.',
            },
        ],
    },
    {
        version: '0.51.0',
        date: '2026-10-17',
        changes: [
            {
                kind: 'fixed',
                text: 'Le chariot d’or glisse désormais sur la route au lieu d’avancer par à-coups.',
                en: 'The gold cart now rolls smoothly along the road instead of moving in jerks.',
            },
            {
                kind: 'improved',
                text: 'Le géant des événements frappe désormais tous les combattants au contact, pas seulement sa cible.',
                en: 'The event giant now strikes every fighter at close range, not just its target.',
            },
            {
                kind: 'improved',
                text: 'Chaque géant des événements a désormais son propre corps, bien plus grand.',
                en: 'Each event giant now has a body of its own, far larger.',
            },
            {
                kind: 'new',
                text: 'Avis de recherche : un tableau sur la place de chaque village propose chaque jour deux primes sur une bête nommée de la région. Prends-en une, abats la bête là où on l’a vue, et reviens au tableau toucher ton or et un matériau.',
                en: 'Wanted posters: a board on every village square offers two bounties a day on a named beast of the region. Take one, slay the beast where it was seen, and come back to the board for your gold and a material.',
            },
            {
                kind: 'new',
                text: 'De nouveaux avis de recherche : cinq bêtes nommées par région, du rat qui vole les baies de l’ermite au yéti qui boit la bière de Torvald. Les tableaux changent davantage d’un jour à l’autre.',
                en: 'New wanted posters: five named beasts per region, from the rat stealing the hermit’s berries to the yeti drinking Torvald’s beer. The boards vary more from one day to the next.',
            },
            {
                kind: 'new',
                text: 'Le tableau s’ouvre d’un clic : chaque avis dit où la bête a été vue, ce qu’elle rapporte et où en est ta chasse. Une fois la prime prise, la mini-carte, la carte et la flèche te mènent jusqu’à elle.',
                en: 'The board opens with a click: each notice tells where the beast was seen, what it pays and how your hunt stands. Once you take a bounty, the minimap, the map and the arrow lead you to it.',
            },
            {
                kind: 'improved',
                text: 'Chariot d’or : le chariot attend au départ dès l’annonce, roule deux fois plus vite, et les pillards attaquent plus souvent et plus nombreux.',
                en: 'Gold cart: the cart waits at the start as soon as the event is announced, rolls twice as fast, and raiders attack more often and in greater numbers.',
            },
            {
                kind: 'improved',
                text: 'Les créatures de nuit ont enfin leur propre apparence.',
                en: 'Night creatures now have their own look.',
            },
            {
                kind: 'improved',
                text: 'Le chariot d’or a enfin son propre dessin.',
                en: 'The gold cart finally has its own sprite.',
            },
            {
                kind: 'fixed',
                text: 'Deux ponts ne mènent plus à un cul-de-sac (Pics enneigés, Prairie).',
                en: 'Two bridges no longer lead to a dead end (Snowy Peaks, Meadow).',
            },
        ],
    },
    {
        version: '0.50.2',
        date: '2026-10-17',
        changes: [
            {
                kind: 'improved',
                text: 'Les boss résistent bien mieux aux héros qui ont plus de trois niveaux de retard sur eux : affrontez-les à votre niveau ou en groupe.',
                en: 'Bosses now hold out far better against heroes more than three levels below them: face them at your level or with a group.',
            },
            {
                kind: 'new',
                text: 'Le carnet de chasse (touche N ou son bouton) : une page par créature de chaque région, avec tes victoires sur elle, son palier et le butin qu’elle t’a déjà laissé. Une créature jamais vaincue n’y est qu’une ombre, et une page au dernier palier prend un cadre doré.',
                en: 'The Hunting Log (N key or its button): one page per creature of each region, with your kills, its tier and the loot it has already dropped for you. A creature never slain is only a shadow there, and a page at the last tier gets a gold frame.',
            },
            {
                kind: 'improved',
                text: 'En changeant de région, son nom s’affiche au-dessus du monde au lieu d’une ligne dans le chat.',
                en: 'Entering a new region now shows its name above the world instead of a line in the chat.',
            },
        ],
    },
    {
        version: '0.50.1',
        date: '2026-10-17',
        changes: [
            {
                kind: 'new',
                text: 'La nuit, certaines créatures paisibles s’endorment et d’autres sortent chasser à leur place : Loup de minuit, Revenant des bois, Noyé des tourbières, Feu des dunes et Gisant des glaces. Elles repartent à l’aube, jamais au milieu d’un combat.',
                en: 'At night, some peaceful creatures fall asleep and others come out to hunt in their place: Midnight Wolf, Wood Revenant, Bog Drowned, Dune Wisp and Ice Sleeper. They leave at dawn, never in the middle of a fight.',
            },
        ],
    },
    {
        version: '0.50.0',
        date: '2026-10-17',
        changes: [
            {
                kind: 'improved',
                text: 'Les monstres rares se repèrent : un appel retentit quand l’un d’eux rôde dans ta région, et de près il brille d’un contour jade, porte un nom de la même couleur et apparaît sur la mini-carte.',
                en: 'Rare monsters stand out: a call rings out when one roams your region, and up close it glows with a jade outline, bears a name of the same colour and shows on the minimap.',
            },
        ],
    },
    {
        version: '0.49.2',
        date: '2026-10-16',
        changes: [
            {
                kind: 'fixed',
                text: 'Plus de grandes étendues vides : le désert, la neige et le marais ont gagné quelques bosquets, cactus et cristaux.',
                en: 'No more great empty stretches: the desert, the snow and the marsh have gained a few copses, cacti and crystals.',
            },
        ],
    },
    {
        version: '0.49.1',
        date: '2026-10-16',
        changes: [
            {
                kind: 'fixed',
                text: 'Les plateaux qu’on ne pouvait que regarder d’en bas ont maintenant un escalier, et quelque chose à trouver en haut.',
                en: 'The plateaus you could only look at from below now have stairs, and something to find at the top.',
            },
        ],
    },
    {
        version: '0.49.0',
        date: '2026-10-16',
        changes: [
            {
                kind: 'new',
                text: 'Deux nouveaux événements mondiaux alternent avec l’invasion gobeline : une élite géante qui rôde autour d’un village et grandit avec le nombre de combattants, et un chariot d’or à escorter de Havrebourg jusqu’à un village, qui n’avance que si quelqu’un l’accompagne et paie un peu d’or à ses escorteurs.',
                en: 'Two new world events now take turns with the Goblin Invasion: a giant elite that prowls around a village and grows with the number of fighters, and a gold cart to escort from Havrebourg to a village, which only moves with someone beside it and pays its escorts a little gold.',
            },
        ],
    },
    {
        version: '0.48.2',
        date: '2026-10-16',
        changes: [
            {
                kind: 'fixed',
                text: 'Le clic droit sur un joueur n’ouvre plus le menu du navigateur par-dessus le sien.',
                en: 'Right-clicking a player no longer opens the browser’s menu over its own.',
            },
        ],
    },
    {
        version: '0.48.1',
        date: '2026-10-16',
        changes: [
            {
                kind: 'new',
                text: 'La météo : de temps en temps, la pluie tombe sur la prairie et la forêt, le brouillard couvre le marais, le blizzard souffle sur les pics et une tempête de sable balaie le désert. Elle ne change rien aux combats.',
                en: 'Weather: now and then, rain falls on the meadow and the forest, fog covers the swamp, a blizzard blows over the peaks and a sandstorm sweeps the desert. It changes nothing in fights.',
            },
            {
                kind: 'improved',
                text: 'Le réglage « Assombrir la nuit » devient « Nuit et météo » : coupé, le monde reste en plein jour et au sec.',
                en: 'The “Darken the night” setting becomes “Night and weather”: turned off, the world stays in full daylight and dry.',
            },
        ],
    },
    {
        version: '0.48.0',
        date: '2026-10-16',
        changes: [
            {
                kind: 'new',
                text: 'Rencontres sur les routes : loin des villages, un colporteur s’arrête quelques minutes avec un objet rare, une voyageuse attaquée par des pillards attend qu’on la secoure, ou un coffre tombé d’une charrette attend d’être ouvert. Chacun n’y a droit qu’une fois, et tous ceux qui ont aidé la voyageuse sont récompensés.',
                en: 'Road encounters: away from the villages, a peddler stops for a few minutes with one rare item, a traveller set upon by raiders waits to be rescued, or a chest fallen off a cart waits to be opened. Each player gets one go, and everyone who helped the traveller is rewarded.',
            },
        ],
    },
    {
        version: '0.47.3',
        date: '2026-10-16',
        changes: [
            {
                kind: 'new',
                text: 'Monstres rares : de temps en temps, une créature unique et nommée (Roussette, Écorce-Noire, Vieille-Mousse, Dard-Rouge, Plume-de-Givre) apparaît quelque part dans sa région, annoncée à ceux qui s’y trouvent, et repart au bout de vingt minutes si personne ne la trouve. Chaque participant reçoit son propre butin : une pièce rare au moins, des matériaux et le trophée de la bête.',
                en: 'Rare monsters: now and then a unique, named creature (Russet, Blackbark, Old Mossback, Red Stinger, Frostplume) appears somewhere in its region, announced to those who are there, and leaves after twenty minutes if nobody finds it. Each participant gets their own loot: at least a rare piece, materials and the beast’s trophy.',
            },
            {
                kind: 'new',
                text: 'Nouveau succès « Chasseur de rares » : vaincre cinq monstres rares, avec son titre.',
                en: 'New “Rare Hunter” achievement: defeat five rare monsters, with its title.',
            },
        ],
    },
    {
        version: '0.47.2',
        date: '2026-10-16',
        changes: [
            {
                kind: 'new',
                text: 'Carnet de chasse : à 10, 100 puis 1 000 victoires sur une même créature, tes coups contre elle gagnent 1 %, 2 % puis 3 % de dégâts, et trois nouveaux titres récompensent les chasseurs les plus assidus (Pisteur, Traqueur, Fléau des Bêtes).',
                en: 'Hunting log: after 10, 100 then 1,000 wins over the same creature, your blows against it deal 1%, 2% then 3% more damage, and three new titles reward the keenest hunters (Tracker, Stalker, Beastbane).',
            },
        ],
    },
    {
        version: '0.47.1',
        date: '2026-10-16',
        changes: [
            {
                kind: 'new',
                text: 'Les créatures ne se battent plus toutes de la même façon : certaines font des rondes autour de leur tanière, d’autres reculent un instant quand elles sont presque vaincues, et les loups, hyènes et hommes-lézards appellent leurs semblables à l’aide quand on les frappe.',
                en: 'Creatures no longer all fight the same way: some patrol around their den, others back away for a moment when nearly beaten, and wolves, hyenas and lizardmen call their kin for help when struck.',
            },
        ],
    },
    {
        version: '0.47.0',
        date: '2026-10-16',
        changes: [
            {
                kind: 'new',
                text: 'Le jour et la nuit : le monde s’assombrit au crépuscule, reste dans la nuit quarante minutes, puis l’aube revient, à une autre heure chaque jour. Réglages : « Assombrir la nuit » garde le plein jour si tu préfères.',
                en: 'Day and night: the world darkens at dusk, stays in the night for forty minutes, then dawn comes back, at a different hour each day. Settings: “Darken the night” keeps full daylight if you prefer.',
            },
        ],
    },
    {
        version: '0.46.0',
        date: '2026-10-16',
        changes: [
            {
                kind: 'fixed',
                text: 'Le chat de zone ne parle plus qu’à la région où tu te trouves, et non plus à tout le monde ouvert.',
                en: 'Zone chat now only reaches the region you are in, no longer the whole open world.',
            },
            {
                kind: 'improved',
                text: 'Chat plus simple : les onglets ne font plus que trier les messages, le bouton devant la saisie (ou la touche Tab) choisit où tu parles, et un clic sur un pseudo lui écrit en privé.',
                en: 'Simpler chat: tabs now only sort messages, the button before the input (or the Tab key) picks where you speak, and clicking a name whispers to them.',
            },
        ],
    },
    {
        version: '0.45.0',
        date: '2026-10-16',
        changes: [
            {
                kind: 'improved',
                text: 'Les collines et les sommets ont maintenant quelque chose à découvrir : un camp et son coffre sur les plus grandes, une ruine gardée par une meute sur les moyennes, une pierre dressée ou une statue au milieu d’un bosquet sur les petites.',
                en: 'Hills and summits now have something to discover: a camp and its chest on the largest, a ruin kept by a pack on the middling ones, a standing stone or a statue in a grove on the small ones.',
            },
        ],
    },
    {
        version: '0.44.4',
        date: '2026-10-15',
        changes: [
            {
                kind: 'improved',
                text: 'Succès : l’objet offert en récompense s’affiche avec son image, à côté du bouton pour le récupérer.',
                en: 'Achievements: the item given as a reward now shows with its picture, next to the button that claims it.',
            },
        ],
    },
    {
        version: '0.44.3',
        date: '2026-10-15',
        changes: [
            {
                kind: 'fixed',
                text: 'Le cœur du Marais Brumeux se traverse enfin : moins de falaises et de roche autour de Brumevase, et des passages dans toutes les directions.',
                en: 'The heart of the Misty Marsh can finally be crossed: fewer cliffs and less rock around Brumevase, and ways through in every direction.',
            },
        ],
    },
    {
        version: '0.44.2',
        date: '2026-10-15',
        changes: [
            {
                kind: 'improved',
                text: 'Grottes : on n’y entre plus en passant devant, il faut cliquer leur entrée.',
                en: 'Caves: walking past one no longer takes you in; click its entrance to go in.',
            },
        ],
    },
    {
        version: '0.44.1',
        date: '2026-10-15',
        changes: [
            {
                kind: 'new',
                text: 'En arrivant au camp, un nouveau personnage voit les commandes de base (marcher, attaquer, changer de cible, ramasser) avec ses propres touches, ou les gestes sur écran tactile, jusqu’à son premier rat.',
                en: 'On arriving at the camp, a new character sees the basic controls (walk, attack, switch target, pick up) with their own keys, or the gestures on a touch screen, until their first rat.',
            },
        ],
    },
    {
        version: '0.44.0',
        date: '2026-10-15',
        changes: [
            {
                kind: 'improved',
                text: 'Camp des premiers pas plus rapide : Ser Givrecoeur va droit au but, trois rats suffisent, et Odon offre le plastron et la rondache, il ne reste que trois pièces à acheter.',
                en: 'A quicker First Steps Camp: Ser Givrecoeur gets straight to the point, three rats are enough, and Odon gives you the breastplate and the buckler, leaving only three pieces to buy.',
            },
        ],
    },
    {
        version: '0.43.6',
        date: '2026-10-15',
        changes: [
            {
                kind: 'fixed',
                text: 'Marche au clavier plus fluide : le héros file droit devant sans à-coups, aussi bien qu’après un clic, et s’arrête sur la case suivante quand on lâche la touche.',
                en: 'Smoother keyboard walking: the hero heads straight on without hitches, as smoothly as after a click, and stops on the next tile when the key is let go.',
            },
        ],
    },
    {
        version: '0.43.5',
        date: '2026-10-14',
        changes: [
            {
                kind: 'fixed',
                text: 'Sur téléphone, le suivi d’une invasion est plus compact et ne couvre plus presque tout l’écran : replié, il ne montre que le lieu, le temps et la vague.',
                en: 'On phones, the invasion tracker is more compact and no longer covers most of the screen: folded, it only shows the place, the time and the wave.',
            },
            {
                kind: 'fixed',
                text: 'Sur téléphone, la sortie des grottes se touche à nouveau : toute l’arche de lumière y mène, et elle ne reste plus cachée sous la barre de sorts et le chat.',
                en: 'On phones, the way out of caves can be tapped again: the whole arch of light leads to it, and it no longer hides under the spell bar and the chat.',
            },
        ],
    },
    {
        version: '0.43.4',
        date: '2026-10-14',
        changes: [
            {
                kind: 'fixed',
                text: 'Les lettres accentuées s’affichent à nouveau dans la police du jeu.',
                en: 'Accented letters show in the game font again.',
            },
        ],
    },
    {
        version: '0.43.3',
        date: '2026-10-14',
        changes: [
            {
                kind: 'fixed',
                text: 'Sur téléphone, le bouton Discord ne se répète plus dans le menu à chaque entrée ou sortie d’une grotte.',
                en: 'On phones, the Discord button no longer repeats in the menu each time you enter or leave a cave.',
            },
        ],
    },
    {
        version: '0.43.2',
        date: '2026-10-14',
        changes: [
            {
                kind: 'fixed',
                text: 'Sur téléphone tenu droit, le journal de quêtes montre la liste, puis la quête touchée en entier avec un bouton pour revenir, au lieu de les mélanger.',
                en: 'On a phone held upright, the quest journal shows the list, then the quest you tap in full with a button to go back, instead of mixing them up.',
            },
        ],
    },
    {
        version: '0.43.1',
        date: '2026-10-14',
        changes: [
            {
                kind: 'improved',
                text: 'L’alerte de vie basse se voit enfin : le cadre rouge au bord de l’écran est bien plus large et plus vif, et bat plus fort quand tu es sur le point de tomber.',
                en: 'The low-health warning is now hard to miss: the red frame around the screen is much wider and brighter, and beats harder when you are about to fall.',
            },
            {
                kind: 'improved',
                text: 'On voit enfin quelles créatures attaquent d’elles-mêmes : le nom d’une créature passive (qui ne riposte que si on la frappe) est gris-bleu au lieu de rouge, et le cadre de cible indique « Passif » ou « Agressif ».',
                en: 'You can now tell which creatures attack on sight: a passive creature (one that only fights back when struck) has a grey-blue name instead of red, and the target frame reads “Passive” or “Aggressive”.',
            },
        ],
    },
    {
        version: '0.43.0',
        date: '2026-10-14',
        changes: [
            {
                kind: 'improved',
                text: 'La carte du monde (M) montre aussi les autres joueurs en vue, comme la mini-carte.',
                en: 'The world map (M) now also shows the other players in sight, like the minimap.',
            },
            {
                kind: 'new',
                text: 'Marcher au clavier : ZQSD (WASD sur un clavier QWERTY), une case par pas tant que la touche est tenue ; le clic marche toujours. Les quatre touches se changent dans Réglages > Touches.',
                en: 'Walk with the keyboard: WASD (ZQSD on an AZERTY keyboard), one tile per step while the key is held; clicking still walks. The four keys can be changed in Settings > Controls.',
            },
            {
                kind: 'improved',
                text: 'Pour laisser la main gauche aux déplacements, la monture passe sur R, la potion rapide sur E et le journal de quêtes sur U (si tu les avais changées, tes touches restent les tiennes).',
                en: 'To leave the left hand free for walking, the mount moves to R, the quick potion to E and the quest journal to U (keys you had changed stay yours).',
            },
            {
                kind: 'new',
                text: 'Tout au clavier : Tab passe à la cible suivante (Espace l’attaque) et C parle au PNJ, ouvre le coffre, prend le portail ou ramasse le butin le plus proche. Les deux touches se changent dans Réglages > Touches.',
                en: 'Play entirely on the keyboard: Tab switches to the next target (Space attacks it) and C talks to the nearest NPC, opens the chest, takes the portal or picks up the loot. Both keys can be changed in Settings > Controls.',
            },
        ],
    },
    {
        version: '0.42.2',
        date: '2026-10-14',
        changes: [
            {
                kind: 'fixed',
                text: 'Cliquer près d’un allié en plein combat n’ouvre plus son menu : le clic gauche sert toujours au jeu, le menu d’un joueur s’ouvre au clic droit (appui long sur écran tactile).',
                en: 'Clicking near an ally mid-fight no longer opens their menu: left click always plays, and a player’s menu opens with a right click (long press on touch screens).',
            },
        ],
    },
    {
        version: '0.42.1',
        date: '2026-10-14',
        changes: [
            {
                kind: 'new',
                text: 'Mini-carte : un clic (ou un toucher) l’agrandit, un autre la ramène à sa taille ; elle s’en souvient.',
                en: 'Minimap: a click (or a tap) enlarges it, another brings it back to its size; it remembers.',
            },
        ],
    },
    {
        version: '0.42.0',
        date: '2026-10-14',
        changes: [
            {
                kind: 'improved',
                text: 'La fenêtre du coffre ressemble désormais à ton sac : des cases, et l’on y glisse ou double-clique un objet pour le déposer ou le retirer.',
                en: 'The chest window now looks like your bag: slots, where you drag or double-click an item to deposit or withdraw it.',
            },
            {
                kind: 'fixed',
                text: 'Sur un téléphone à l’horizontale, la fiche d’un objet ne sort plus de l’écran : ses boutons restent toujours visibles.',
                en: 'On a phone held sideways, an item’s card no longer runs off the screen: its buttons always stay in sight.',
            },
        ],
    },
    {
        version: '0.41.7',
        date: '2026-10-14',
        changes: [
            {
                kind: 'fixed',
                text: 'Le coffre dit clairement qu’un objet lié n’y entre pas, même s’il a été reçu par ton compte.',
                en: 'The chest now says plainly that a bound item can’t go in, even one your account received.',
            },
        ],
    },
    {
        version: '0.41.6',
        date: '2026-10-14',
        changes: [
            {
                kind: 'improved',
                text: 'Réglages : choisir une touche déjà prise échange les deux touches, plus besoin d’en passer par une troisième.',
                en: 'Settings: picking a key that is already taken swaps the two keys, no need to go through a third one anymore.',
            },
        ],
    },
    {
        version: '0.41.5',
        date: '2026-10-14',
        changes: [
            {
                kind: 'new',
                text: 'Réglages › Affichage : « Afficher le chat » permet de masquer le chat ; il réapparaît le temps d’écrire (Entrée, ou le bouton Chat sur téléphone).',
                en: 'Settings › Display: “Show chat” hides the chat; it comes back while you type (Enter, or the Chat button on a phone).',
            },
        ],
    },
    {
        version: '0.41.4',
        date: '2026-10-14',
        changes: [
            {
                kind: 'fixed',
                text: 'Grottes : la sortie vers la vallée se voit de loin, une arche de jour au pied de la paroi.',
                en: 'Caves: the way out to the valley now shows from afar, an arch of daylight at the foot of the wall.',
            },
        ],
    },
    {
        version: '0.41.3',
        date: '2026-10-14',
        changes: [
            {
                kind: 'improved',
                text: 'L’infobulle d’un objet lié explique ce que ça veut dire : il reste avec ton personnage, ne s’échange pas, ne passe ni à l’Hôtel des ventes ni au Coffre, mais le marchand le rachète.',
                en: 'A bound item’s tooltip now explains what it means: it stays with your character, can’t be traded, put up at the Auction House or stored in the Chest, but the Merchant still buys it.',
            },
        ],
    },
    {
        version: '0.41.2',
        date: '2026-10-14',
        changes: [
            {
                kind: 'improved',
                text: 'Un objet au sol sous la souris s’encadre et son nom s’éclaire : on sait lequel le clic ramasse.',
                en: 'An item on the ground under the mouse gets a frame and its name lights up: you know which one a click picks up.',
            },
        ],
    },
    {
        version: '0.41.1',
        date: '2026-10-14',
        changes: [
            {
                kind: 'new',
                text: 'Mordu alors que tu ne fais rien, ton personnage riposte tout seul contre le monstre qui l’attaque. Un clic au sol suffit pour fuir.',
                en: 'Bitten while idle, your character now strikes back at the monster on its own. Click the ground to run away.',
            },
        ],
    },
    {
        version: '0.41.0',
        date: '2026-10-14',
        changes: [
            {
                kind: 'improved',
                text: 'Avant une mise à jour, le serveur prévient et affiche un compte à rebours, puis te ramène en jeu tout seul dès qu’il revient : plus de déconnexion sans explication.',
                en: 'Before an update, the server warns you with a countdown, then brings you back into the game on its own as soon as it returns: no more unexplained disconnections.',
            },
            {
                kind: 'improved',
                text: 'Les barres de défilement des fenêtres sont dessinées aux couleurs du jeu, en pixels, au lieu de celles du système.',
                en: 'Window scrollbars are now drawn in the game’s own pixel style and colours instead of the system’s.',
            },
        ],
    },
    {
        version: '0.40.0',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'Les boss de région reviennent une minute après leur chute, avec un compte à rebours dans leur antre : plus besoin d’attendre pour retenter ou aider un ami.',
                en: 'Region bosses come back one minute after they fall, with a countdown in their lair: no more waiting to try again or to help a friend.',
            },
            {
                kind: 'improved',
                text: 'Le butin d’un boss est désormais le butin du jour : une fois par jour et par personnage, ta première victoire sur lui en donne deux et toute son expérience ; les suivantes rapportent un dixième de l’expérience et aucun butin.',
                en: 'A boss’s loot is now the loot of the day: once a day per character, your first win against it pays two loots and all its experience; later wins pay a tenth of the experience and no loot.',
            },
            {
                kind: 'improved',
                text: 'L’interface écrit enfin le français avec ses accents, ses guillemets « » et ses apostrophes, dans la police du jeu.',
                en: 'The interface now writes French with its accents, « » quotes and apostrophes, in the game’s own font.',
            },
            {
                kind: 'fixed',
                text: 'La flèche des quêtes ne se cache plus sous l’interface (barre de sorts, chat, mini-carte, suivi des quêtes) : elle se pose juste à côté.',
                en: 'The quest arrow no longer hides under the interface (spell bar, chat, minimap, quest tracker): it waits right beside it.',
            },
        ],
    },
    {
        version: '0.39.0',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'Les boss de région tiennent plus longtemps : environ une minute et demie pour un guerrier seul bien équipé.',
                en: 'Region bosses last longer: about a minute and a half for a well-equipped Warrior alone.',
            },
            {
                kind: 'improved',
                text: 'Les attaques annoncées au sol par un boss font très mal : chacune emporte une grosse part de ta vie, quelle que soit ton armure. Sors des cases rouges !',
                en: 'The attacks a boss marks on the ground now hurt badly: each takes a large share of your health, whatever your armour. Get out of the red tiles!',
            },
            {
                kind: 'improved',
                text: 'Une créature de plus de trois niveaux au-dessus de toi encaisse moins tes coups et frappe plus fort, à chaque niveau d’écart : on ne bat plus un yéti du niveau 20 au niveau 11.',
                en: 'A creature more than three levels above you takes less from your blows and hits harder, for every level of the gap: a level-11 hero can no longer beat a level-20 yeti.',
            },
        ],
    },
    {
        version: '0.38.1',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'La fiche d’un objet lié dans le chat montre aussi son image.',
                en: 'The card of an item linked in the chat now shows its picture too.',
            },
        ],
    },
    {
        version: '0.38.0',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'Le cadre de groupe, à gauche, te montre aussi toi-même en première ligne : ta tête, ton niveau et ta vie.',
                en: 'The party frame on the left now shows you too, on the first line: your head, level and health.',
            },
        ],
    },
    {
        version: '0.37.3',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Ta barre de vie, plus large et cerclée de clair, se voit désormais au-dessus des monstres.',
                en: 'Your health bar, wider and rimmed in light, now shows above the monsters.',
            },
        ],
    },
    {
        version: '0.37.2',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Le butin d’un coffre est réservé à celui qui l’ouvre : en groupe, chacun ramasse le sien.',
                en: 'A chest’s loot belongs to whoever opens it: in a party, everyone picks up their own.',
            },
        ],
    },
    {
        version: '0.37.1',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'Chat moins encombré : un refus (sort pas encore prêt, coffre trop loin, pas de monture…) s’affiche un instant au-dessus du monde au lieu de remplir le chat, et un même message répété se compte sur sa ligne (×3).',
                en: 'A tidier chat: a refusal (spell not ready yet, chest too far, no mount…) now shows briefly over the world instead of filling the chat, and the same notice repeated counts up on its own line (×3).',
            },
            {
                kind: 'fixed',
                text: 'Quêtes des marques du dragon : les créatures à abattre t’attendent sur place, à toi et à ton groupe, tant que la quête est en cours (la Saignée, la Ferme calcinée, l’Empreinte, la Plaine de verre, la Brèche, le Bourbier du Troll).',
                en: 'Dragon mark quests: the creatures to slay now wait for you and your party at the place itself while the quest is under way (the Scar, the Charred Farm, the Clawprint, the Glass Plain, the Breach, the Troll’s Mire).',
            },
            {
                kind: 'new',
                text: 'Trois créatures changées par le dragon : le Renard calciné, le Sylvain fou et le Caïman affolé.',
                en: 'Three creatures changed by the dragon: the Charred Fox, the Maddened Treant and the Frenzied Caiman.',
            },
            {
                kind: 'improved',
                text: 'Une action refusée (sort en recharge, pas de monture, coffre vide…) s’affiche un instant au-dessus du jeu au lieu d’encombrer le chat, et un même message répété dans le chat se compte au lieu de s’empiler.',
                en: 'A refused action (spell cooling down, no mount, empty chest…) now shows for a moment above the game instead of cluttering the chat, and the same chat notice repeated counts up instead of stacking.',
            },
            {
                kind: 'fixed',
                text: 'Cartes : le point de quête est plus grand et ressort mieux, sur la mini-carte comme sur la carte du monde.',
                en: 'Maps: the quest marker is bigger and stands out better, on the minimap as on the world map.',
            },
        ],
    },
    {
        version: '0.37.0',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'Monture : en combat, sa case dans la barre des sorts s’assombrit comme une recharge et montre quand tu pourras remonter en selle.',
                en: 'Mount: in combat, its slot in the spell bar darkens like a cooldown and shows when you can get back in the saddle.',
            },
        ],
    },
    {
        version: '0.36.4',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Quêtes de camp : un camp nettoyé et son coffre ouvert juste avant de prendre la quête comptent maintenant, plus besoin d’attendre le retour des gardiens.',
                en: 'Camp quests: a camp cleared and its chest opened just before taking the quest now counts; no more waiting for the guards to come back.',
            },
        ],
    },
    {
        version: '0.36.3',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'Échelles des sommets et des grottes : on ne descend ni ne monte plus en passant dessus, il faut cliquer l’échelle.',
                en: 'Summit and cave ladders: walking over one no longer takes you down or up; click the ladder to climb it.',
            },
        ],
    },
    {
        version: '0.36.2',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Fondeur : la fenêtre montre les pièces que tu portes et précise qu’elles ne comptent pas dans la fusion ; retire-les pour les fusionner.',
                en: 'Smelter: the window shows the pieces you wear and makes clear they do not count towards fusion; take them off to fuse them.',
            },
        ],
    },
    {
        version: '0.36.1',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Une potion de vie n’est plus bue quand ta vie est déjà au maximum : elle reste dans ton sac.',
                en: 'A health potion is no longer drunk when your health is already full: it stays in your bag.',
            },
        ],
    },
    {
        version: '0.36.0',
        date: '2026-10-13',
        title: 'La carte au doigt',
        titleEn: 'A map at your fingertips',
        changes: [
            {
                kind: 'improved',
                text: 'Carte du monde sur téléphone, tablette et petit écran d’ordinateur : elle prend tout l’écran et s’ouvre sur toi, assez grande pour lire chaque nom ; fais-la glisser du doigt, pince touche + et − ou la molette pour zoomer, ◎ pour revenir sur toi. Les régions, les événements et la légende s’ouvrent depuis la barre du bas, et toucher une région y emmène la carte.',
                en: 'World Map on phones, tablets and small computer screens: it fills the screen and opens on you, large enough to read every name; drag it with a finger, pinch, tap + and − or use the wheel to zoom, ◎ to come back to you. Regions, events and the key open from the bar at the bottom, and tapping a region takes the map there.',
            },
        ],
    },
    {
        version: '0.35.0',
        date: '2026-10-13',
        title: 'Inspection et objets liés',
        titleEn: 'Inspect and item links',
        changes: [
            {
                kind: 'new',
                text: 'Inspecter un joueur : clique-le puis « Inspecter » pour voir son personnage, ses statistiques et chaque pièce qu’il porte, avec sa fiche au survol.',
                en: 'Inspect a player: click them, then “Inspect”, to see their character, their stats and every piece they wear, each with its card on hover.',
            },
            {
                kind: 'new',
                text: 'Lier un objet dans le chat : Maj+clic sur un objet de ton inventaire (ou « Lier dans le chat » sur sa fiche au toucher) ; un clic sur l’objet lié montre sa fiche, amélioration et affixes compris.',
                en: 'Link an item in the Chat: Shift+click an item in your Inventory (or “Link in Chat” on its card on a touch screen); a click on the linked item shows its card, upgrade and affixes included.',
            },
        ],
    },
    {
        version: '0.34.1',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Butin au sol : un objet clignote pendant ses 10 dernières secondes avant de disparaître (sac plein, par exemple au donjon).',
                en: 'Loot on the ground: an item blinks during its last 10 seconds before it vanishes (full bag, in a dungeon for instance).',
            },
        ],
    },
    {
        version: '0.34.0',
        date: '2026-10-13',
        changes: [
            {
                kind: 'new',
                text: 'Inventaire : on peut détruire un objet du sac. Bouton « Détruire » à côté de « Ranger » (puis clic sur l’objet, ou glisser l’objet dessus), ou bouton de la fiche sur téléphone ; une fenêtre demande confirmation et, pour une pile, combien en détruire. Un appât prêté par une quête en cours ne se détruit pas.',
                en: 'Inventory: you can destroy an item from your bag. « Destroy » button next to « Sort » (then click the item, or drag the item onto it), or the item card’s button on a phone; a window asks for confirmation and, for a stack, how many to destroy. A bait lent by a quest under way cannot be destroyed.',
            },
        ],
    },
    {
        version: '0.33.5',
        date: '2026-10-13',
        changes: [
            {
                kind: 'improved',
                text: 'Le Marais Brumeux respire : deux fois moins de marécage, moins de buttes et moins d’arbres morts, de larges passages partout et Brumevase ouvert de tous côtés.',
                en: 'The Misty Marsh breathes: half as much bog, fewer mounds and dead trees, wide ways everywhere, and Brumevase open on every side.',
            },
        ],
    },
    {
        version: '0.33.4',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Brumevase est enfin desservi : la route de Havrebourg ne s’arrête plus à l’entrée du marais et mène jusqu’au village, et le Marais Brumeux compte bien moins de buttes infranchissables, donc plus de passages.',
                en: 'Brumevase is finally within easy reach: the road from Havrebourg no longer stops at the edge of the swamp but runs all the way to the village, and the Misty Marsh has far fewer impassable mounds, so more ways through.',
            },
        ],
    },
    {
        version: '0.33.3',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Dans un donjon ou une grotte plus petits que l’écran, la carte est centrée sur un fond sombre au lieu d’être collée à gauche sur du vert.',
                en: 'In a dungeon or cave smaller than the screen, the map is centred on a dark backdrop instead of stuck to the left on green.',
            },
        ],
    },
    {
        version: '0.33.2',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Après un message, un clic dans le monde (sur une créature, le sol…) quitte le chat : les touches des sorts relancent les sorts au lieu d’écrire dans le champ.',
                en: 'After sending a message, clicking in the world (a creature, the ground…) leaves the chat: spell keys cast spells again instead of typing in the field.',
            },
            {
                kind: 'fixed',
                text: 'Hôtel des ventes : un lot acheté arrive aussitôt dans le sac, et la bourse se met à jour, sans avoir à se reconnecter. Le vendeur, lui, voit son or arriver tout de suite.',
                en: 'Auction house: a lot you buy shows up in your bag at once and your purse updates, without logging in again. The seller sees their gold arrive right away too.',
            },
        ],
    },
    {
        version: '0.33.1',
        date: '2026-10-13',
        changes: [
            {
                kind: 'fixed',
                text: 'Pendant une invasion gobeline, les assaillants n’arrivent plus sur une hauteur d’où ils ne pouvaient pas descendre vers le village : l’invasion ne reste plus bloquée.',
                en: 'During a goblin invasion, raiders no longer arrive on a height they could not climb down from to the village: the invasion no longer gets stuck.',
            },
            {
                kind: 'fixed',
                text: 'Les boutons latéraux de la souris (4 et 5) ne font plus quitter la page par précédent / suivant : ils s’assignent comme des touches dans les Réglages.',
                en: 'The mouse side buttons (4 and 5) no longer leave the page as back / forward: they can be bound like keys in the Settings.',
            },
            {
                kind: 'fixed',
                text: 'Survoler le chat ne décale plus ses lignes : la place de la barre de défilement est toujours gardée, elle ne fait que s’afficher.',
                en: 'Hovering the chat no longer shifts its lines: the scrollbar’s room is always kept, it only shows up.',
            },
            {
                kind: 'fixed',
                text: 'La fenêtre d’un habitant (dialogue de quête, forgeron, marchand, coffre…) se ferme d’elle-même quand tu t’éloignes de lui, au lieu de rester ouverte.',
                en: 'A townsperson’s window (quest dialogue, smith, merchant, chest…) now closes by itself when you walk away from them, instead of staying open.',
            },
        ],
    },
    {
        version: '0.33.0',
        date: '2026-10-12',
        title: 'Merci aux bêta-testeurs',
        titleEn: 'Thank you, beta testers',
        webOnlyTitle: true,
        changes: [
            {
                kind: 'new',
                text: 'Chaque compte ouvert pendant la bêta reçoit le succès « Bêta-testeur » : le titre du même nom, et Glitch, un petit bug apprivoisé qui grésille à tes côtés, à réclamer dans l’onglet Exploits des succès (touche J). Merci d’avoir essuyé les plâtres avec nous !',
                webOnly: true,
                en: 'Every account opened during the beta gets the “Beta Tester” achievement: the title of the same name, and Glitch, a small tamed bug that crackles at your side, to claim in the Feats tab of the achievements (J key). Thank you for braving the early days with us!',
            },
            {
                kind: 'improved',
                text: 'Une pastille sur le bouton Succès, sur l’onglet Succès et sur celui de la région compte les récompenses qui attendent d’être récupérées.',
                en: 'A badge on the Achievements button, on the Achievements tab and on the region’s tab counts the rewards waiting to be claimed.',
            },
        ],
    },
    {
        version: '0.32.1',
        date: '2026-10-12',
        changes: [
            {
                kind: 'improved',
                text: 'Les paroles au-dessus des personnages s’affichent dans une bulle sombre bordée, avec une pointe vers celui qui parle, au lieu d’un texte jaune posé sur le décor.',
                en: 'Words over characters now show in a dark framed bubble pointing at the speaker, instead of yellow text laid over the scenery.',
            },
            {
                kind: 'improved',
                text: 'Un message du chat fait 60 caractères au plus, et la bulle au-dessus de celui qui parle l’affiche en entier.',
                en: 'A chat message is now at most 60 characters, and the bubble over the speaker shows it in full.',
            },
        ],
    },
    {
        version: '0.32.0',
        date: '2026-10-12',
        title: 'Le classement',
        titleEn: 'The leaderboard',
        changes: [
            {
                kind: 'new',
                text: 'Un classement des aventuriers (touche L, ou le podium en haut à droite) : les cinquante meilleurs par expérience ou par DPS, chacun avec son portrait tel qu’il est équipé, sur tout le serveur, parmi tes amis ou dans ta guilde. Ta propre place est toujours en vert, même loin derrière.',
                en: 'A leaderboard of adventurers (L key, or the podium at the top right): the top fifty by experience or by DPS, each with their portrait as they are geared, across the whole server, among your friends or in your guild. Your own place is always in green, even far behind.',
            },
        ],
    },
    {
        version: '0.31.4',
        date: '2026-10-12',
        changes: [
            {
                kind: 'fixed',
                text: 'Les grottes et les donjons font partie du monde : le nombre de joueurs en ligne compte tout le monde, où qu’il soit, et le chat garde ses messages quand tu entres ou sors d’une grotte.',
                en: 'Caves and dungeons are part of the world: the online player count includes everyone wherever they are, and the chat keeps its messages when you enter or leave a cave.',
            },
            {
                kind: 'fixed',
                text: 'La molette fait défiler le chat pour relire les anciens messages, et un nouveau message ne te ramène plus en bas pendant que tu lis.',
                en: 'The mouse wheel scrolls the chat back to older messages, and a new message no longer jumps you to the bottom while you read.',
            },
            {
                kind: 'improved',
                text: 'La bulle au-dessus d’un joueur qui parle tient sur une ligne : un long message est coupé par « … » et se lit en entier dans le chat.',
                en: 'The bubble over a talking player fits on one line: a long message is cut with “…” and reads in full in the chat.',
            },
            {
                kind: 'fixed',
                text: 'En groupe, une créature tuée par un membre qui n’a pas la quête ne fait plus avancer la tienne : il faut que le tueur la chasse aussi.',
                en: 'In a party, a creature killed by a member who does not have the quest no longer advances yours: the killer must be hunting it too.',
            },
        ],
    },
    {
        version: '0.31.3',
        date: '2026-10-12',
        changes: [
            {
                kind: 'fixed',
                text: 'Changer de direction en pleine marche ne fait plus reculer le personnage d’une case : le serveur tourne exactement là où tu as tourné.',
                en: 'Changing direction mid-walk no longer pulls your character back a tile: the server now turns exactly where you did.',
            },
            {
                kind: 'fixed',
                text: 'Sur la mini-carte, les autres joueurs se voient aussi dans les villages : leur point passe devant le portail et les pictogrammes, cerclé de sombre.',
                en: 'On the minimap, other players now show in villages too: their dot is drawn over the portal and the pictograms, with a dark rim.',
            },
            {
                kind: 'fixed',
                text: 'Chez le marchand et à l’hôtel des ventes, le bouton « Acheter » s’écrit en or quand l’achat est possible : il ne paraît plus grisé.',
                en: 'At the merchant’s and the auction house, the « Buy » button is written in gold when you can buy: it no longer looks greyed out.',
            },
            {
                kind: 'fixed',
                text: 'On ne marche plus sur le bord nord des rivières, des lacs et des marécages : ton personnage n’a plus les pieds dans l’eau.',
                en: 'You no longer walk on the north edge of rivers, lakes and bogs: your character no longer stands with their feet in the water.',
            },
            {
                kind: 'fixed',
                text: 'Chez le forgeron et le fondeur, la liste des pièces ne déborde plus sur le côté quand elle défile, et sa barre de défilement prend les couleurs du jeu.',
                en: 'At the smith’s and the smelter’s, the list of pieces no longer spills sideways when it scrolls, and its scrollbar takes the game’s colours.',
            },
        ],
    },
    {
        version: '0.31.2',
        date: '2026-10-12',
        changes: [
            {
                kind: 'improved',
                text: 'La langue se choisit dans une liste déroulante des réglages (Affichage), prête pour les langues à venir.',
                en: 'The language is now picked from a drop-down list in the settings (Display), ready for languages to come.',
            },
        ],
    },
    {
        version: '0.31.1',
        date: '2026-10-12',
        changes: [
            {
                kind: 'fixed',
                text: 'Changer de langue dans les réglages tient enfin : le jeu ne repasse plus dans l’ancienne langue au rechargement.',
                en: 'Changing the language in the settings now sticks: the game no longer switches back to the old language when it reloads.',
            },
        ],
    },
    {
        version: '0.31.0',
        date: '2026-10-06',
        title: 'Plus de confort',
        titleEn: 'More comfort',
        changes: [
            {
                kind: 'new',
                text: 'État absent : après 5 minutes sans rien faire, ou avec /absent, « Absent » s’affiche au-dessus de ton nom, et tes amis, ta guilde et ton groupe le voient dans leurs listes. Ta prochaine action te ramène.',
                en: 'Away status: after 5 minutes without doing anything, or with /absent, « Away » shows above your name, and your friends, guild and party see it in their lists. Your next action brings you back.',
            },
            {
                kind: 'new',
                text: 'Potion rapide : une case à côté des sorts (touche A, ou un bouton sur écran tactile) boit la potion de vie la mieux adaptée sans ouvrir le sac.',
                en: 'Quick potion: a slot beside your spells (A key, or a button on touch screens) drinks the best-suited health potion without opening the bag.',
            },
            {
                kind: 'new',
                text: 'Sous un quart de ta vie, les bords de l’écran battent en rouge et ton cœur se fait entendre, plus vite quand la chute est proche (désactivable dans Réglages › Affichage).',
                en: 'Below a quarter of your health, the screen’s edges pulse red and you hear your heartbeat, faster when you are about to fall (can be turned off in Settings › Display).',
            },
            {
                kind: 'new',
                text: 'Un bouton « Ranger » trie le sac : équipement, consommables puis matériaux, du plus rare au plus commun, et regroupe les piles incomplètes d’un même objet.',
                en: 'A “Sort” button tidies the bag: equipment, consumables then materials, rarest first, and merges partial stacks of the same item.',
            },
            {
                kind: 'new',
                text: 'Une flèche verte signale les pièces meilleures que celles que tu portes : dans le sac, chez le marchand, à l’hôtel des ventes et au sol.',
                en: 'A green arrow marks gear better than what you wear: in your bag, at the merchant, at the Auction House and on the ground.',
            },
            {
                kind: 'new',
                text: 'Un coffre dans chaque village, partagé par tous les personnages de ton compte : 20 places pour garder tes objets ou les faire passer d’un personnage à l’autre (ni objets liés, ni apparences).',
                en: 'A chest in every village, shared by all the characters on your account: 20 slots to store items or pass them from one character to another (no bound items, no looks).',
            },
            {
                kind: 'fixed',
                text: 'Un compte dont un personnage porte une traînée de pas ne pouvait plus se connecter.',
                en: 'An account with a character wearing a footstep trail could no longer sign in.',
            },
            {
                kind: 'improved',
                text: 'Forgeron, alchimiste, fondeur et graveur de runes : fenêtres refaites comme celle du marchand. La bourse reste visible, chaque matériau montre ce que tu as et ce qu’il faut (en rouge s’il en manque), le bouton dit ce qu’il fait et combien il coûte, et l’alchimiste prépare le nombre de potions que tu choisis.',
                en: 'Smith, alchemist, smelter and runesmith: windows redone like the merchant’s. Your purse stays in sight, each material shows what you have against what it takes (red when short), the button says what it does and what it costs, and the alchemist brews as many potions as you choose.',
            },
            {
                kind: 'improved',
                text: 'L’hôtel des ventes fait peau neuve : bourse toujours visible, lots en cartes avec leur prix, et la vente se fait directement dans la fenêtre (quantité, prix, commission et ce que tu recevras), sans ouvrir le sac à côté.',
                en: 'The Auction House gets a makeover: your purse always in view, lots as cards with their price, and selling happens right in the window (quantity, price, fee and what you will receive), without opening your bag beside it.',
            },
            {
                kind: 'improved',
                text: 'L’entrée dans le monde est plus légère, surtout sur téléphone.',
                en: 'Getting into the world is lighter, especially on phones.',
            },
        ],
    },
    {
        version: '0.30.0',
        date: '2026-10-06',
        title: 'Un camp plus accueillant',
        titleEn: 'A friendlier camp',
        changes: [
            {
                kind: 'improved',
                text: 'Le camp des premiers pas tient en quatre étapes au lieu de cinq : Garin te donne directement les cinq épées à fondre et le silex de Hald, et la forge fait partie de sa leçon.',
                en: 'The starting camp now has four steps instead of five: Garin hands you the five swords to melt and Hald’s flint himself, and the forge is part of his lesson.',
            },
            {
                kind: 'new',
                text: 'Au camp, une bulle sous la mini-carte dit quoi faire ensuite, et le bouton, l’objet ou la marchandise à utiliser se met à briller.',
                en: 'In the camp, a bubble under the minimap says what to do next, and the button, item or ware to use starts glowing.',
            },
            {
                kind: 'new',
                text: 'Après une fusion, le fondeur propose d’équiper tout de suite la pièce obtenue.',
                en: 'After a fusion, the smelter offers to equip the new piece right away.',
            },
            {
                kind: 'improved',
                text: 'Ser Givrecoeur t’accueille dès ton arrivée au camp, et mourir au camp ne coûte plus d’expérience. Les rats y mordent moins fort.',
                en: 'Ser Givrecoeur greets you as soon as you arrive in the camp, and dying there no longer costs experience. Its rats bite softer, too.',
            },
            {
                kind: 'fixed',
                text: 'On ne peut plus se bloquer au camp en revendant les épées, l’épée rare ou le silex, ni en renforçant une autre pièce que l’épée rare.',
                en: 'You can no longer get stuck in the camp by selling the swords, the rare sword or the flint, or by strengthening anything but the rare sword.',
            },
        ],
    },
    {
        version: '0.29.0',
        date: '2026-10-06',
        title: 'Le marchand fait peau neuve',
        titleEn: 'A new look for the merchant',
        changes: [
            {
                kind: 'improved',
                text: 'La fenêtre du marchand fait peau neuve : la vente se fait dans la fenêtre même, chaque objet avec son prix, des filtres par sorte et « Vendre tout le butin » d’un coup ; à l’achat, l’équipement et les provisions sont rangés à part, avec la quantité au choix et la bourse toujours en vue. Plus besoin d’ouvrir le sac à côté, ce qui rend le marchand bien plus simple sur téléphone.',
                en: 'The merchant’s window got a makeover: selling happens right in it, each item with its price, filters by kind and “Sell all loot” in one go; when buying, gear and supplies are listed apart, with a quantity to choose and your purse always in view. No need to open the bag beside it anymore, which makes the merchant much easier on a phone.',
            },
        ],
    },
    {
        version: '0.28.3',
        date: '2026-10-02',
        changes: [
            {
                kind: 'improved',
                text: 'Les dialogues des quêtes sont plus simples et plus détendus : chaque personnage dit clairement ce qu’il attend de toi, avec une pointe d’humour.',
                en: 'Quest dialogue is simpler and more relaxed: every character clearly says what they need from you, with a touch of humor.',
            },
            {
                kind: 'new',
                text: 'Cinq nouvelles coiffures à la création du personnage : cheveux longs, queue de cheval, tresse, carré et boucles.',
                en: 'Five new hairstyles when creating a character: long hair, ponytail, braid, bob and curls.',
            },
            {
                kind: 'new',
                text: 'Corps féminin à la création du personnage : il porte toute l’armure du jeu, comme le corps masculin.',
                en: 'Feminine body when creating a character: it wears every piece of armour in the game, just like the masculine one.',
            },
            {
                kind: 'improved',
                text: 'Chez le graveur de runes, l’avant et l’après de la dernière gravure s’effacent dès que tu prépares la suivante.',
                en: 'At the runesmith, the before and after of the last engraving clear as soon as you set up the next one.',
            },
        ],
    },
    {
        version: '0.28.2',
        date: '2026-10-01',
        changes: [
            {
                kind: 'fixed',
                text: 'Sur téléphone, la fenêtre de dialogue des personnages ne dépasse plus de l’écran, et l’écran de création du personnage commence sous les boutons de langue et de son.',
                en: 'On phones, the characters’ dialogue window no longer runs off the screen, and the character creation screen starts below the language and sound buttons.',
            },
        ],
    },
    {
        version: '0.28.1',
        date: '2026-10-01',
        changes: [
            {
                kind: 'improved',
                text: 'Sur écran tactile, le bouton Attaquer en bas à droite disparaît : touche une créature pour aller la frapper, l’écran est plus dégagé.',
                en: 'On touch screens, the Attack button at the bottom right is gone: tap a creature to go and strike it, and the screen is less cluttered.',
            },
            {
                kind: 'new',
                text: 'Tu peux bloquer un joueur : « Bloquer » dans son menu, ou /bloquer nom. Tu ne vois plus ses messages, et il ne peut plus t’écrire en privé ni t’inviter. La fenêtre Amis liste les joueurs bloqués, pour les débloquer.',
                en: 'You can block a player: “Block” in their menu, or /bloquer name. You no longer see their messages, and they can no longer whisper to you or invite you. The Friends window lists blocked players, to unblock them.',
            },
        ],
    },
    {
        version: '0.28.0',
        date: '2026-10-01',
        changes: [
            {
                kind: 'improved',
                text: 'Le jeu se joue aussi téléphone à la verticale : plus de bandeau pour te demander de le tourner, et le nom du lieu passe sous la mini-carte.',
                en: 'The game now plays with your phone held upright too: no more banner asking you to turn it, and the place name moves under the minimap.',
            },
            {
                kind: 'new',
                text: 'Tu peux supprimer ton compte depuis l’écran de tes personnages : tous tes personnages et leurs objets disparaissent pour toujours.',
                en: 'You can delete your account from your characters screen: all your characters and their items are gone forever.',
            },
            {
                kind: 'fixed',
                webOnly: true,
                text: 'Sur un téléphone à la verticale, la fenêtre du profil et les cartes de la boutique ne dépassent plus de l’écran.',
                en: 'On a phone held upright, the profile window and the shop cards no longer run off the screen.',
            },
        ],
    },
    {
        version: '0.27.1',
        date: '2026-10-01',
        changes: [
            {
                kind: 'improved',
                webOnly: true,
                text: 'Nouveaux prix des packs : l’Aurore de la Compagnie passe à 19,99 €, et tous les autres packs, hors pack fondateur, à 9,99 €. Les pièces à l’unité suivent, et les ailes deviennent la pièce la plus chère de leur pack.',
                en: 'New pack prices: the Aurora of the Company now costs €19.99, and every other pack except the Founder Pack €9.99. Single pieces follow suit, and wings become the priciest piece of their pack.',
            },
            {
                kind: 'fixed',
                text: 'Les montures sont de nouveau visibles : un cavalier ne flotte plus au-dessus de son aura, Ser Givrecoeur a retrouvé Frimas.',
                en: 'Mounts are visible again: a rider no longer floats above their aura, and Ser Givrecoeur has found Frimas again.',
            },
        ],
    },
    {
        version: '0.27.0',
        date: '2026-10-01',
        changes: [
            {
                kind: 'new',
                webOnly: true,
                text: 'Nouveau pack dans la boutique : l’Aurore de la Compagnie. Six pièces d’argent blanc où glissent les reflets verts et violets de l’aurore, un cœur de lumière dans la cuirasse, des ailes en rideaux de lumière et un renard polaire, tout animé, avec le titre « Veilleur de l’Aurore ».',
                en: 'New pack in the shop: the Aurora of the Company. Six pieces of white silver where the green and violet glints of the aurora glide, a heart of light in the cuirass, wings made of curtains of light and a polar fox, all animated, with the title “Aurora Watcher”.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Les traînées de pas arrivent dans la boutique : braises, givre, pétales ou feuilles d’automne dans ton sillage. Elles se portent dans la nouvelle case Traînée de l’onglet Apparence.',
                en: 'Footstep trails arrive in the shop: embers, frost, petals or autumn leaves in your wake. Wear them in the new Trail slot of the Appearance tab.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Nouveaux compagnons dans la boutique : le Chat noir, le Slime, le Mini-golem et le Mini-yéti trottent à tes côtés, et le Loup de guerre te porte deux fois et demie plus vite que tes jambes.',
                en: 'New companions in the shop: the Black Cat, the Slime, the Mini Golem and the Mini Yeti trot at your side, and the War Wolf carries you two and a half times faster than your legs.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Six nouveautés à l’unité dans la boutique : ailes de papillon, d’ange et de chauve-souris qui battent dans ton dos, et trois armes d’apparence, l’épée de cristal, la lance-étendard et le poireau de combat.',
                en: 'Six new singles in the shop: butterfly, angel and bat wings beating at your back, and three look weapons, the Crystal Sword, the Banner Spear and the Battle Leek.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Le Pack de l’Épouvantail arrive dans la boutique pour Halloween, du 15 octobre au 5 novembre seulement : une tête de citrouille où vacille une chandelle, des haillons bourrés de paille, gants et bottes assortis, une faux, une lanterne des moissons et un corbeau de minuit, avec le titre « L’Épouvantail qui marche ».',
                en: 'The Scarecrow Pack arrives in the shop for Halloween, from 15 October to 5 November only: a pumpkin head with a flickering candle inside, straw-stuffed rags, matching gloves and boots, a scythe, a harvest lantern and a midnight crow, with the title "The Walking Scarecrow".',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Le Pack du Tueur de dragon arrive dans la boutique : les trophées d’un wyrm abattu, crâne, ossements et cuir tanné gravés de runes rouges qui battent et crachent des braises, son croc et son écaille, un griffon à monter et un faucon de chasse, avec le titre « Tueur de Wyrm ».',
                en: 'The Dragonslayer Pack arrives in the shop: the trophies of a slain wyrm, skull, bones and tanned leather carved with red runes that pulse and spit embers, its fang and its scale, a griffon to ride and a hunting falcon, with the title “Wyrmslayer”.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Nouveau pack dans la boutique, « Esprit sylvestre » : la parure d’une dryade (bois de cerf en fleurs et lucioles, écorce et mousse, lame-branche fleurie, bouclier de racines), un cerf blanc à monter et le titre « Esprit de la Forêt ».',
                en: 'New pack in the shop, “Sylvan Spirit”: a dryad’s garb (blossoming antlers and fireflies, bark and moss, a flowering branch blade, a shield of roots), a white stag to ride and the title “Spirit of the Forest”.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Nouveau pack dans la boutique, « Sorcier des marais » : une capuche où luisent des champignons, une robe de mousse, un bâton de racine au feu follet, une lanterne à flamme verte, la grenouille-lanterne à tes côtés et le titre « Maître des Tourbières ».',
                en: 'New pack in the shop, “Bog Sorcerer”: a hood of glowing mushrooms, a mossy robe, a root staff holding a will-o’-the-wisp, a green-flamed lantern, the lantern frog at your side and the title “Master of the Bogs”.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Nouveau pack dans la boutique : le Djinn des mirages, six pièces de soie bleue et d’or où fume une magie bleue, et un tapis volant qui te porte au-dessus du sable. Avec lui, le titre « Seigneur des Mirages ».',
                en: 'New pack in the shop: the Djinn of Mirages, six pieces of blue and gold silk wreathed in blue magic smoke, and a flying carpet that carries you above the sand. It comes with the title "Lord of Mirages".',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Nouveau pack dans la boutique : le Chaman des pics, fourrures de loup, os et runes d’ocre qui s’éveillent, et un harfang des neiges qui vole à tes côtés. Avec lui, le titre « Voix des Esprits ».',
                en: 'New pack in the shop: the Shaman of the Peaks, wolf furs, bone and ochre runes that wake, and a snowy owl flying at your side. It comes with the title "Voice of the Spirits".',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Chaque nouveau pack a sa traînée de pas, comprise dans le pack et vendue seule à 3,99 € : aurore, cendres, paille, lucioles, spores, sable ou neige.',
                en: 'Each new pack has its own footstep trail, included in the pack and sold on its own for €3.99: aurora, ashes, straw, fireflies, spores, sand or snow.',
            },
            {
                kind: 'improved',
                webOnly: true,
                text: 'Le Pack du Dragon passe à 8,99 €, toujours bien moins cher que ses pièces une à une.',
                en: 'The Dragon Pack now costs €8.99, still far cheaper than its pieces bought one by one.',
            },
            {
                kind: 'improved',
                text: 'Tous les dialogues des quêtes sont réécrits, du camp aux Pics Gelés : chaque habitant a désormais sa propre voix, ses soucis et son avis sur ses voisins.',
                en: 'Every quest dialogue has been rewritten, from the camp to the Frozen Peaks: each villager now has a voice of their own, their own worries and opinions about the neighbours.',
            },
        ],
    },
    {
        version: '0.26.0',
        date: '2026-09-30',
        changes: [
            {
                kind: 'new',
                text: 'La garde-robe : tes cosmétiques, tes ailes, tes familiers et tes montures se rangent à part, sans limite de place, et ne prennent plus aucune case du sac. L’onglet Apparence de l’inventaire n’affiche plus qu’eux.',
                en: 'The Wardrobe: your cosmetics, wings, pets and mounts are now kept apart, with no limit on space, and no longer take up any Bag slot. The Appearance tab of the inventory now shows only them.',
            },
            {
                kind: 'fixed',
                text: 'Un boss ne se soigne plus entièrement quand sa cible tombe : il se tourne vers le joueur suivant.',
                en: 'A boss no longer heals back to full when its target falls: it turns on the next player.',
            },
            {
                kind: 'fixed',
                text: 'En raid de six joueurs ou plus, la barre de vie de Shefira affiche sa vraie vie.',
                en: 'In raids of six or more, Shefira’s health bar shows her true health.',
            },
            {
                kind: 'fixed',
                text: 'L’alliance affiche les noms de l’autre groupe même quand ses membres sont hors ligne.',
                en: 'The alliance shows the other party’s names even while its members are offline.',
            },
        ],
    },
    {
        version: '0.25.0',
        date: '2026-09-30',
        title: 'La dernière garde',
        titleEn: 'The Last Stand',
        changes: [
            {
                kind: 'new',
                text: 'La fin de l’histoire : une fois les cinq recrues réunies, Ser Givrecoeur rassemble la Compagnie pour affronter Shefira dans son Aire, à la Brèche des Pics Gelés.',
                en: 'The end of the story: once the five recruits are gathered, Ser Givrecoeur musters the Company to face Shefira in her Aerie, at the Breach in the Frozen Peaks.',
            },
            {
                kind: 'new',
                text: 'Raid de 3 à 10 joueurs contre Shefira au niveau 20, en groupe ou en alliance de deux groupes : balistes, braseros de givre et cor de la Compagnie, butin une fois par semaine.',
                en: 'A raid of 3 to 10 players against Shefira at level 20, as a party or an alliance of two parties: the Company’s ballistae, frost braziers and horn, loot once a week.',
            },
            {
                kind: 'new',
                text: 'Le set de Shefira, épique et légendaire, le meilleur équipement du jeu, et le Serment de Givrecoeur, une épée plus grande que toutes les autres.',
                en: 'Shefira’s set, epic and legendary, the best gear in the game, and Givrecoeur’s Oath, a sword larger than any other.',
            },
            {
                kind: 'improved',
                text: 'Les bulles de dialogue au-dessus des personnages vont à la ligne au lieu de couper la phrase, et restent affichées plus longtemps quand elles sont longues.',
                en: 'Speech bubbles above characters now wrap onto new lines instead of cutting the sentence off, and stay up longer when they are long.',
            },
            {
                kind: 'improved',
                text: 'Quand tu acceptes « La dernière garde », Ser Givrecoeur et la Compagnie quittent le quartier et t’attendent au bord du gouffre de la Brèche.',
                en: 'When you accept “The Last Stand”, Ser Givrecoeur and the Company leave their quarter and wait for you at the edge of the chasm at the Breach.',
            },
            {
                kind: 'improved',
                text: 'Le mémorial de la Compagnie : les cinq stèles forment un arc autour du monument, chacune au prénom de son mort, et les noms ne se chevauchent plus.',
                en: 'The Company’s memorial: the five steles now stand in an arc around the monument, each bearing the first name of its fallen, and the names no longer overlap.',
            },
            {
                kind: 'improved',
                text: 'Raid de Shefira : ses attaques annoncées au sol frappent bien plus fort. Rester dans un souffle ou sous un piqué coûte la moitié de ta vie, deux de suite te tuent. L’histoire garde ses dégâts.',
                en: 'Shefira’s raid: her telegraphed attacks hit much harder. Standing in a breath or under a dive costs half your health, and two in a row kill you. The story keeps its damage.',
            },
            {
                kind: 'new',
                text: 'Une musique épique rien que pour le combat contre Shefira, dans son Aire.',
                en: 'An epic music track of its own for the fight against Shefira, in her Aerie.',
            },
            {
                kind: 'improved',
                text: 'Les quêtes rapportent bien plus d’or, selon l’expérience qu’elles donnent : suivre une chaîne paie désormais autant que la chasse. La fin de l’histoire est la plus généreuse du jeu.',
                en: 'Quests now pay much more gold, in line with the experience they give: following a chain pays as well as hunting. The end of the story is the most generous in the game.',
            },
            {
                kind: 'new',
                text: 'Abattre Shefira en raid rend « Le serment des cinq » : Ulfar te remet le Serment de Givrecoeur légendaire et le Drake de Shefira, une monture aussi rapide que le cheval de feu.',
                en: 'Slaying Shefira in a raid completes “The Oath of the Five”: Ulfar hands you the legendary Givrecoeur’s Oath and Shefira’s Drake, a mount as fast as the fire horse.',
            },
            {
                kind: 'improved',
                text: 'Nouveau logo : Shefira déploie ses ailes de feu derrière le nom du jeu.',
                en: 'New logo: Shefira spreads her wings of fire behind the game’s name.',
            },
            {
                kind: 'improved',
                text: 'L’équipement compte bien plus : les armures donnent environ deux fois et demie plus de vie, la défense protège mieux contre les monstres de haut niveau, et un personnage nu gagne moins de vie en montant de niveau. Au niveau 20, sans équipement, Shefira ne pardonne plus.',
                en: 'Gear matters far more: armour gives about two and a half times more health, defence protects better against high-level monsters, and a naked character gains less health as it levels up. At level 20 without gear, Shefira no longer forgives.',
            },
        ],
    },
    {
        version: '0.24.0',
        date: '2026-09-29',
        title: 'La Compagnie Givrecoeur',
        titleEn: 'The Givrecoeur Company',
        changes: [
            {
                kind: 'new',
                text: 'Le jeu est disponible en anglais : interface, objets, créatures, quêtes et dialogues. La langue suit celle de ton navigateur et se change sur l’accueil ou dans les réglages (Affichage).',
                en: 'The game is now available in English: interface, items, creatures, quests and dialogue. It follows your browser’s language, and you can switch it on the home screen or in the settings (Display).',
            },
            {
                kind: 'improved',
                text: 'Forêt, marais et désert : chaque route vers la région suivante passe désormais par la marque de Shefira et le recrutement de Mélisande, Nella ou Méret. Dans les Pics, le serment d’Ulfar suit toujours la chute du Géant.',
                en: 'Forest, marsh and desert: every road to the next region now runs through Shefira’s mark and the recruitment of Mélisande, Nella or Méret. In the Peaks, Ulfar’s oath still follows the Giant’s fall.',
            },
            {
                kind: 'improved',
                text: 'Avant l’incendie, Gautier parle de sa moisson et d’une lueur inquiétante au nord. Après la chute du Grand Épouvantail, il promet de rebâtir sa ferme.',
                en: 'Before the fire, Gautier talks about his harvest and an unsettling glow to the north. After the Great Scarecrow falls, he vows to rebuild his farm.',
            },
            {
                kind: 'new',
                text: 'Le Camp des premiers pas est désormais le camp de recrutement de la Compagnie Givrecoeur. Ser Givrecoeur a vu la glace se fendre sous Givrecime, et personne ne l’a cru ; Odon le marchand, Garin le fondeur et Hald le forgeron sont ses premiers compagnons. Les leçons restent les mêmes, et tu pars pour Havrebourg avec sa lettre au capitaine Aldric.',
                en: 'The First Steps Camp is now the recruiting camp of the Givrecoeur Company. Ser Givrecoeur saw the ice crack beneath Givrecime, and nobody believed him; Odon the merchant, Garin the smelter and Hald the blacksmith are his first companions. The lessons stay the same, and you set off for Havrebourg with his letter to Captain Aldric.',
            },
            {
                kind: 'new',
                text: 'Prairie : après le Grand Épouvantail, la Guetteuse Maëlle t’envoie à la Ferme calcinée. Dans ses cendres, le fermier Gautier a trouvé une écaille rouge sang, encore chaude, et l’Ermite Aubépin lit son nom dans les pierres : Shefira. Trois nouvelles quêtes.',
                en: 'Meadow: after the Great Scarecrow, Lookout Maëlle sends you to the Burnt Farm. In its ashes, farmer Gautier found a blood-red scale, still warm, and Hermit Aubépin reads its name in the stones: Shefira. Three new quests.',
            },
            {
                kind: 'improved',
                text: 'Prairie : la ferme de Gautier reste debout, avec sa grange rouge et ses champs de blé, tant que tu n’es pas descendu affronter le Grand Épouvantail. C’est cette nuit-là que Shefira la brûle. Et l’écaille fait désormais partie de la chaîne : le capitaine ne te donne ton cheval qu’une fois Maëlle entrée dans la Compagnie.',
                en: 'Meadow: Gautier’s farm stays standing, with its red barn and wheat fields, until you go down to face the Great Scarecrow. That is the night Shefira burns it. And the scale is now part of the chain: the captain only gives you your horse once Maëlle has joined the Company.',
            },
            {
                kind: 'new',
                text: 'Forêt des Murmures : après le Loup-Garou, la Druidesse Mélisande te mène à la Saignée, une ligne d’arbres brûlés d’un seul souffle, et fait parler les souches. Trois nouvelles quêtes.',
                en: 'Whispering Forest: after the Werewolf, Druid Mélisande leads you to the Scar, a line of trees burnt in a single breath, and makes the stumps speak. Three new quests.',
            },
            {
                kind: 'new',
                text: 'Marais Brumeux : après le Troll des Tourbières, la Traqueuse Nella suit une trace qui tombe du ciel jusqu’à l’Empreinte, et l’Érudit Théophane retrouve la bête dans les textes noyés. Trois nouvelles quêtes.',
                en: 'Misty Marsh: after the Bog Troll, Tracker Nella follows a trail that falls from the sky to the Clawprint, and Scholar Théophane finds the beast in the drowned texts. Three new quests.',
            },
            {
                kind: 'new',
                text: 'Maëlle, Mélisande et Nella peuvent rejoindre la Compagnie : chacune a sa quête de recrue, rendue à Ser Givrecoeur à Havrebourg.',
                en: 'Maëlle, Mélisande and Nella can join the Company: each has her own recruit quest, turned in to Ser Givrecoeur in Havrebourg.',
            },
            {
                kind: 'new',
                text: 'Havrebourg a son quartier de la Compagnie, au nord de la place : un bastion ceint de murs, où l’on monte par une allée dallée entre les braseros, gardée par deux sentinelles en blanc. Dans la cour, Ser Givrecoeur attend devant le donjon, une flamme brûle encore dans la glace au pied de l’épée plantée, et chaque recrue que tu réunis vient t’attendre autour de la table de guerre. Chacun voit les siennes : c’est ton voyage. Le quartier a sa marque sur la carte et la mini-carte.',
                en: 'Havrebourg has its Company Quarter, north of the square: a walled bastion reached by a paved path between braziers, guarded by two sentries in white. In the courtyard, Ser Givrecoeur waits before the keep, a flame still burns in the ice at the foot of the planted sword, and every recruit you gather comes to wait for you around the war table. Everyone sees their own: it’s your journey. The Quarter has its marker on the map and the minimap.',
            },
            {
                kind: 'new',
                text: 'Le dragon a laissé ses marques sur la carte : une ferme calcinée, une saignée d’arbres brûlés, un cratère de griffes, une plaine de verre noir et une brèche dans la glace. La fumée y monte encore.',
                en: 'The dragon has left its marks on the map: a burnt farm, a scar of burnt trees, a crater of claws, a plain of black glass and a breach in the ice. Smoke still rises there.',
            },
            {
                kind: 'improved',
                text: 'Le pégase de Ser Givrecoeur s’appelle désormais Frimas, pour qu’on ne le confonde plus avec une autre créature.',
                en: 'Ser Givrecoeur’s pegasus is now called Rime, so it’s no longer mistaken for another creature.',
            },
            {
                kind: 'improved',
                text: 'Les créatures de la Prairie, de la Forêt et du Marais rapportent un peu moins d’expérience, pour que leurs nouvelles quêtes ne raccourcissent pas la région.',
                en: 'Meadow, Forest and Marsh creatures give a little less experience, so their new quests don’t make the region shorter.',
            },
            {
                kind: 'new',
                text: 'Désert de Braise : après le Chacal d’Or, la Prêtresse Méret t’envoie à la Plaine de verre, près de l’Obélisque Oublié, où le sable a fondu en une nuit. Ce que Néfer y relève révèle enfin le secret du roi sans nom, et le serment dont Méret a hérité. Trois nouvelles quêtes.',
                en: 'Ember Desert: after the Golden Jackal, Priestess Méret sends you to the Glass Plain, near the Forgotten Obelisk, where the sand melted in a single night. What Néfer finds there finally reveals the secret of the nameless king, and the oath Méret inherited. Three new quests.',
            },
            {
                kind: 'new',
                text: 'Pics Gelés : la chute du Géant de Givre n’est plus la fin de l’histoire. La montagne gronde, la glace se fend, et la chaîne continue jusqu’à la Brèche. Quatre nouvelles quêtes, et une rencontre que Givrecime n’oubliera pas.',
                en: 'Frozen Peaks: the Frost Giant’s fall is no longer the end of the story. The mountain rumbles, the ice cracks, and the chain goes on to the Breach. Four new quests, and an encounter Givrecime won’t forget.',
            },
            {
                kind: 'new',
                text: 'Shefira se montre enfin. À la Ferme calcinée, son ombre passe sur les champs et l’on entend gronder au loin ; à la Brèche, elle descend du ciel devant toi, se pose dans un nuage de poussière, crie son nom à faire trembler la montagne, puis repart vers les sommets.',
                en: 'Shefira finally shows herself. At the Burnt Farm, her shadow sweeps over the fields and a rumble echoes in the distance; at the Breach, she comes down from the sky before you, lands in a cloud of dust, roars her name loud enough to shake the mountain, then flies back to the peaks.',
            },
            {
                kind: 'new',
                text: 'La Prêtresse Méret et le Vieil Ulfar rejoignent la Compagnie Givrecoeur à Havrebourg quand tu as fini leur région.',
                en: 'Priestess Méret and Old Ulfar join the Givrecoeur Company in Havrebourg once you’ve finished their region.',
            },
            {
                kind: 'improved',
                text: 'Les répliques de la Skalde Runa, de la Voyante Eira et de leurs voisins des Pics, comme celles de Méret et de Néfer au désert, laissent deviner ce qui dort sous la glace.',
                en: 'The lines of Skald Runa, Seer Eira and their neighbors in the Peaks, like those of Méret and Néfer in the desert, hint at what sleeps beneath the ice.',
            },
            {
                kind: 'improved',
                text: 'Les créatures du désert rapportent un peu moins d’expérience, pour que la chaîne, plus longue, garde le même rythme.',
                en: 'Desert creatures give a little less experience, so the longer chain keeps the same pace.',
            },
            {
                kind: 'improved',
                text: 'Carte du monde : les noms ne se chevauchent plus. Chacun se place à côté de son repère, là où il y a de la place, et un lieu trop à l’étroit montre son nom au survol.',
                en: 'World map: names no longer overlap. Each one sits next to its marker, wherever there’s room, and a place too cramped shows its name on hover.',
            },
        ],
    },
    {
        version: '0.23.0',
        date: '2026-09-29',
        title: 'L’intro en quêtes',
        titleEn: 'The Intro as Quests',
        changes: [
            {
                kind: 'new',
                text: 'Le Camp des premiers pas se suit maintenant en quêtes, comme le reste du jeu : Ser Givrecoeur, le marchand, le fondeur et le forgeron du camp te confient chacun une leçon, avec leurs « ! » et « ? », le journal (Q) et la flèche qui te guide. La dernière quête t’envoie au capitaine de Havrebourg.',
                en: 'The First Steps Camp now plays out as quests, like the rest of the game: Ser Givrecoeur and the camp’s merchant, smelter and blacksmith each give you a lesson, with their “!” and “?”, the journal (Q) and the arrow that guides you. The last quest sends you to the captain of Havrebourg.',
            },
            {
                kind: 'new',
                text: 'Tu as déjà fini l’intro avec un autre personnage ? Ser Givrecoeur peut t’envoyer directement à Havrebourg avec l’équipement du camp, épée rare à +1 comprise.',
                en: 'Already finished the intro with another character? Ser Givrecoeur can send you straight to Havrebourg with the camp’s equipment, rare +1 sword included.',
            },
            {
                kind: 'improved',
                text: 'Le bandeau du haut de l’écran disparaît dans le camp : la vue reste dégagée, et la fenêtre de Ser Givrecoeur explique le pourquoi de chaque étape.',
                en: 'The banner at the top of the screen disappears in the camp: the view stays clear, and Ser Givrecoeur’s window explains the why of each step.',
            },
            {
                kind: 'improved',
                text: 'La souris a ses propres curseurs en pixel art : une flèche d’acier, une main qui pointe sur ce qui répond au clic, une main ouverte sur ce qui se déplace.',
                en: 'The mouse has its own pixel-art cursors: a steel arrow, a pointing hand over whatever responds to a click, an open hand over whatever moves.',
            },
            {
                kind: 'improved',
                text: 'Un clic sur une forteresse de donjon, l’entrée d’une grotte ou une trappe suffit pour y entrer : plus besoin de viser la porte.',
                en: 'One click on a dungeon fortress, a cave entrance or a trapdoor is enough to go in: no more aiming for the door.',
            },
        ],
    },
    {
        version: '0.22.1',
        date: '2026-09-29',
        changes: [
            {
                kind: 'fixed',
                text: 'Camp des premiers pas : chaque nouveau joueur a maintenant ses propres rats sur le terrain d’entraînement. Personne ne peut plus te les prendre ni empêcher leur retour en restant à côté.',
                en: 'First Steps Camp: every new player now has their own rats on the training ground. Nobody can take them from you or stop them from coming back by standing next to them.',
            },
        ],
    },
    {
        version: '0.22.0',
        date: '2026-09-29',
        changes: [
            {
                kind: 'new',
                webOnly: true,
                text: 'Codes cadeaux : un champ en bas de la boutique accepte un code offert (par un créateur, un concours…). Ses objets arrivent dans le sac du personnage joué, liés à lui ; un code ne sert qu’une fois par compte.',
                en: 'Gift codes: a field at the bottom of the Shop accepts a gifted code (from a creator, a contest…). Its items arrive in the bag of the character you’re playing, bound to them; a code works only once per account.',
            },
        ],
    },
    {
        version: '0.21.0',
        date: '2026-09-29',
        title: 'Les grottes refaites',
        titleEn: 'Caves Remade',
        changes: [
            {
                kind: 'new',
                text: 'Les grottes sous les sommets sont plus grandes et ne se ressemblent plus : galeries sinueuses, grandes cavernes, dédales de salles ou halls en lacets, chacune avec la roche et le sol de sa région.',
                en: 'The caves beneath the summits are bigger and no longer look alike: winding galleries, great caverns, mazes of rooms or switchback halls, each with the rock and floor of its region.',
            },
            {
                kind: 'new',
                text: 'De nouveaux décors dans les grottes : stalagmites de roche, de glace ou de grès, cristaux, champignons lumineux, flaques, mousse, toiles d’araignée, torches et ossements, placés comme dans une vraie grotte : stalagmites en groupes contre la roche, éboulis au pied des parois, flaques en nappes, toiles dans les recoins.',
                en: 'New decor in the caves: stalagmites of rock, ice or sandstone, crystals, glowing mushrooms, puddles, moss, spider webs, torches and bones, placed as in a real cave: stalagmites in clusters against the rock, scree at the foot of the walls, puddles in pools, webs in the corners.',
            },
            {
                kind: 'improved',
                text: 'Le coffre d’une grotte est au fond d’un caveau gardé par quatre créatures : il reste enchaîné tant qu’elles sont debout et ne s’ouvre qu’à ceux qui les ont combattues. Il est aussi bien mieux rempli. Les créatures des grottes attaquent toutes à vue.',
                en: 'A cave’s chest sits at the back of a vault guarded by four creatures: it stays chained while they stand and only opens for those who fought them. It is also much better stocked. Cave creatures all attack on sight.',
            },
            {
                kind: 'fixed',
                text: 'Entrer dans une grotte ou en sortir ne fait plus descendre de monture.',
                en: 'Entering or leaving a cave no longer dismounts you.',
            },
            {
                kind: 'fixed',
                text: 'Le jeu ne se fige plus près de deux secondes en sortant d’une grotte.',
                en: 'The game no longer freezes for nearly two seconds when leaving a cave.',
            },
            {
                kind: 'improved',
                text: 'L’aura d’un équipement entièrement à +10 s’embrase comme une pièce légendaire : un anneau de feu parcouru de lumière, des flammes qui lèchent son sommet et des braises qui s’envolent.',
                en: 'The aura of equipment fully at +10 blazes like a legendary piece: a ring of fire shot through with light, flames licking at its top and embers drifting up.',
            },
        ],
    },
    {
        version: '0.20.1',
        date: '2026-09-29',
        changes: [
            {
                kind: 'new',
                text: 'Deux familiers à gagner en fin de région : le Renardeau de Sylvebois, confié par le trappeur Corvin après le Loup-Garou, et le Petit caïman de Brumevase, éclos chez la Traqueuse Nella après le Troll des Tourbières.',
                en: 'Two pets to earn at the end of a region: the Sylvebois Fox Cub, entrusted to you by Trapper Corvin after the Werewolf, and the Little Brumevase Caiman, hatched at Tracker Nella’s after the Bog Troll.',
            },
            {
                kind: 'fixed',
                text: 'Les reflets, liserés et flammes de rareté ne se dessinent plus par-dessus la monture.',
                en: 'Rarity shines, trims and flames are no longer drawn over the mount.',
            },
            {
                kind: 'fixed',
                text: 'Changer de familier le montre aussitôt à côté de soi, sans recharger la page.',
                en: 'Changing pets shows the new one at your side right away, without reloading the page.',
            },
            {
                kind: 'fixed',
                text: 'Appeler sa monture en marchant arrête le personnage sur place : il ne file plus jusqu’au bout du chemin pour être ramené en arrière une fois en selle.',
                en: 'Calling your mount while walking stops your character on the spot: they no longer run to the end of the path only to be pulled back once in the saddle.',
            },
            {
                kind: 'improved',
                text: 'Un compte invité est prévenu avant de se déconnecter : ses personnages disparaîtraient avec lui. Une fenêtre propose de l’enregistrer, et le rappelle aussi à la fin des quêtes de la Prairie.',
                en: 'A guest account is warned before logging out: its characters would vanish with it. A window offers to save it, and reminds you again at the end of the Meadow quests.',
            },
            {
                kind: 'improved',
                text: 'Les maisons, tavernes, alchimistes et quelques échoppes des villages n’ont plus de dalle, de plancher ni de tapis d’herbe ou de sable sous leurs murs : ils se posent directement sur le sol.',
                en: 'Village houses, taverns, alchemists and a few stalls no longer have a slab, floorboards or a patch of grass or sand under their walls: they sit right on the ground.',
            },
            {
                kind: 'fixed',
                text: 'Onde sismique : elle porte à 7 cases au lieu de 5 et file droit sur ta cible, en diagonale comprise, en frappant tous les ennemis sur son passage ; sur une cible trop loin, elle est refusée au lieu de partir dans le vide.',
                en: 'Seismic Wave: it reaches 7 tiles instead of 5 and runs straight at your target, diagonals included, hitting every enemy in its path; on a target that’s too far, it’s refused instead of going off into the void.',
            },
            {
                kind: 'improved',
                text: 'Colère du titan : elle frappe désormais à 3 cases autour de toi au lieu de 2, et la terre se déchire pour de bon : colonne de lumière, fissures de lave, pics de roche, rochers projetés et secousse de l’écran.',
                en: 'Titan’s Wrath: it now strikes 3 tiles around you instead of 2, and the earth really tears apart: a pillar of light, lava cracks, rock spikes, flying boulders and a screen shake.',
            },
        ],
    },
    {
        version: '0.20.0',
        date: '2026-09-29',
        title: 'La rareté se voit',
        titleEn: 'Rarity You Can See',
        changes: [
            {
                kind: 'new',
                text: 'La rareté d’une pièce se voit maintenant sur le personnage : un reflet traverse les pièces rares, les épiques s’entourent par moments d’un liseré violet, et les légendaires brûlent d’un liseré de flammes qui ne s’éteint jamais et laissent s’échapper des éclats de leur région (pétales, feuilles, spores, braises, flocons). Un cosmétique porté par-dessus garde la rareté de la vraie pièce.',
                en: 'A piece’s rarity now shows on your character: a shine sweeps across rare pieces, epics are wrapped from time to time in a purple trim, and legendaries burn with a trim of flames that never goes out and shed sparks of their region (petals, leaves, spores, embers, snowflakes). A cosmetic worn on top keeps the rarity of the real piece.',
            },
        ],
    },
    {
        version: '0.19.1',
        date: '2026-09-29',
        title: 'Des falaises qui tiennent debout',
        titleEn: 'Cliffs That Stand Tall',
        changes: [
            {
                kind: 'improved',
                text: 'Les falaises ont été redessinées : le mur remplit toute la hauteur qu’on ne peut pas franchir, les flancs est et ouest montrent leur roche, et le bord des plateaux est souligné. Une terrasse se lit enfin comme un bloc surélevé.',
                en: 'Cliffs have been redrawn: the wall fills the whole height you can’t cross, the east and west sides show their rock, and the edge of plateaus is outlined. A terrace finally reads as a raised block.',
            },
            {
                kind: 'fixed',
                text: 'Les ombres des falaises tombent au pied du mur, nettes, au lieu de flotter une case plus bas ou de faire des traînées floues.',
                en: 'Cliff shadows fall crisply at the foot of the wall, instead of floating one tile lower or leaving blurry streaks.',
            },
            {
                kind: 'fixed',
                text: 'Plus de murs coupés, de morceaux de roche au mauvais endroit ni de carrés gris sur le sol autour des reliefs.',
                en: 'No more cut-off walls, bits of rock in the wrong place or gray squares on the ground around the terrain.',
            },
        ],
    },
    {
        version: '0.19.0',
        date: '2026-09-29',
        title: 'Le pas léger',
        titleEn: 'Light on Your Feet',
        changes: [
            {
                kind: 'improved',
                text: 'Votre personnage marche un peu plus vite : 10 % de plus qu’avant.',
                en: 'Your character walks a bit faster: 10% more than before.',
            },
            {
                kind: 'new',
                text: 'Toutes les bottes donnent de la vitesse de déplacement, de 3 % pour des sabots communs de la Prairie à 15 % pour des bottes légendaires des Pics. Ce bonus dépend de la rareté et du niveau de la paire, jamais de son amélioration chez le forgeron. Il compte aussi à cheval.',
                en: 'All boots give movement speed, from 3% for common Meadow clogs to 15% for legendary Peaks boots. This bonus depends on the pair’s rarity and level, never on its upgrade at the blacksmith. It also counts on horseback.',
            },
            {
                kind: 'fixed',
                text: 'Les montures vont enfin à la vitesse annoncée : le Cheval de Havrebourg était à peine plus rapide qu’un marcheur, et le Chameau allait aussi vite que le Cheval de feu.',
                en: 'Mounts finally go at their advertised speed: the Havrebourg Horse was barely faster than someone on foot, and the Camel was as fast as the Fire Horse.',
            },
        ],
    },
    {
        version: '0.18.0',
        date: '2026-09-29',
        title: 'Les runes',
        titleEn: 'Runes',
        changes: [
            {
                kind: 'new',
                text: 'Les pièces trouvées portent des runes : une pour une rare, deux pour une épique, trois pour une légendaire. DPS, Vie, Défense, chance et dégâts critiques, recharge des sorts, énergie, vol de vie ou dégâts aux boss et élites : deux légendaires du même set ne se valent plus.',
                en: 'Found pieces carry runes: one for a rare, two for an epic, three for a legendary. DPS, Health, Defense, critical chance and damage, spell cooldown, energy, lifesteal or damage to bosses and elites: two legendaries from the same set are no longer equal.',
            },
            {
                kind: 'new',
                text: 'Le Graveur de runes, à Sablerouge et à Givrecime, regrave les runes d’une pièce contre de l’or et des matériaux rares : pour chaque rune, gardez-la, relancez seulement sa valeur, tirez-en une autre, ou scellez-la pour toujours (une relique par rune dont vous gardez le type, trois pour en sceller une).',
                en: 'The Runesmith, in Sablerouge and Givrecime, re-engraves a piece’s runes for gold and rare materials: for each rune, keep it, reroll only its value, draw a new one, or seal it forever (one relic per rune whose type you keep, three to seal one).',
            },
            {
                kind: 'new',
                text: 'Les pièces déjà dans vos sacs ont reçu leurs runes.',
                en: 'The pieces already in your bags have received their runes.',
            },
            {
                kind: 'improved',
                text: 'La fiche du personnage montre le DPS moyen, critiques compris, et tout ce que vos runes ajoutent ; l’infobulle donne le jet minimum et maximum de chaque rune et les compare à celles de la pièce portée.',
                en: 'The character sheet shows average DPS, crits included, and everything your runes add; the tooltip gives each rune’s minimum and maximum roll and compares them with those of the piece you’re wearing.',
            },
            {
                kind: 'improved',
                text: 'Prairie et Forêt : les créatures lâchent plus souvent leurs matériaux, et les améliorations +1 à +3 de leurs pièces coûtent un quart d’or en moins chez le forgeron.',
                en: 'Meadow and Forest: creatures drop their materials more often, and +1 to +3 upgrades of their pieces cost a quarter less gold at the blacksmith.',
            },
        ],
    },
    {
        version: '0.17.2',
        date: '2026-09-29',
        changes: [
            {
                kind: 'improved',
                text: 'La Charge du Guerrier porte à 6 cases, frappe bien plus fort et étourdit la cible trois secondes.',
                en: 'The Warrior’s Charge reaches 6 tiles, hits much harder and stuns the target for three seconds.',
            },
            {
                kind: 'fixed',
                text: 'Les gardiens d’un camp ne reviennent plus un à un pendant qu’on le nettoie : tout le camp revient ensemble, une fois vidé.',
                en: 'A camp’s guards no longer come back one by one while you clear it: the whole camp returns together once emptied.',
            },
            {
                kind: 'improved',
                text: 'Les créatures ne réapparaissent plus sous vos yeux : elles attendent que vous vous éloigniez, ou reviennent à l’autre bout de leur tanière si vous y restez.',
                en: 'Creatures no longer respawn before your eyes: they wait for you to move away, or come back at the far end of their den if you stay.',
            },
            {
                kind: 'improved',
                text: 'Renards, sangliers, sylvains, crapauds, scorpions et ours polaires sont passifs : ils n’attaquent que si on les frappe, et seulement celui qui les frappe.',
                en: 'Foxes, boars, treants, toads, scorpions and polar bears are passive: they only attack if you hit them, and only the one who hits them.',
            },
            {
                kind: 'fixed',
                text: 'Quête « Le Camp des Gobelins » : la Druidesse Mélisande vous envoie au camp dressé sur son propre pic, à quelques pas, au lieu de vous demander d’aller repérer un camp à l’autre bout de la forêt.',
                en: 'Quest “The Goblin Camp”: Druid Mélisande sends you to the camp pitched on her own peak, a few steps away, instead of asking you to scout a camp at the other end of the forest.',
            },
        ],
    },
    {
        version: '0.17.1',
        date: '2026-09-29',
        changes: [
            {
                kind: 'fixed',
                text: 'Le pont des Écuries de Havrebourg ne pose plus un pied dans le lac : ses deux bouts arrivent sur la berge.',
                en: 'The Havrebourg Stables bridge no longer sets a foot in the lake: both ends land on the shore.',
            },
        ],
    },
    {
        version: '0.17.0',
        date: '2026-09-29',
        changes: [
            {
                kind: 'new',
                text: 'Une flèche dorée mène aux camps et aux lieux que demande une quête en cours, et passe par la grotte quand le but est au sommet d’une montagne.',
                en: 'A golden arrow leads to the camps and places an active quest asks for, and goes through the cave when the goal is at the top of a mountain.',
            },
            {
                kind: 'new',
                text: 'La première défaite face à un boss ouvre une fenêtre qui rappelle comment revenir plus fort : forgeron, potions et set de la région.',
                en: 'Your first defeat against a boss opens a window reminding you how to come back stronger: blacksmith, potions and the region’s set.',
            },
            {
                kind: 'new',
                text: 'Un pont à côté des Écuries de Havrebourg ouvre la route directe vers la Forêt des Murmures.',
                en: 'A bridge next to the Havrebourg Stables opens the direct road to the Whispering Forest.',
            },
            {
                kind: 'improved',
                text: 'Les créatures se rangent par niveau : les plus faibles près des routes et des villages, les plus fortes au plus profond de chaque région. Dans la Prairie, les rats d’abord, puis les renards, et les sangliers loin des murs.',
                en: 'Creatures are sorted by level: the weakest near roads and villages, the strongest deep in each region. In the Meadow, rats first, then foxes, and boars far from the walls.',
            },
            {
                kind: 'improved',
                text: 'Forêt des Murmures : le bûcheron Anselme demande désormais d’abattre les archers gobelins de sa piste (fini les deux chasses aux loups d’affilée), des sylvains rôdent près de son camp, et le camp gobelin qui le jouxtait est monté au sommet du Mont Moussu.',
                en: 'Whispering Forest: woodcutter Anselme now asks you to take down the goblin archers on his trail (no more two wolf hunts in a row), treants prowl near his camp, and the goblin camp next door has moved up to the top of Mossy Mount.',
            },
            {
                kind: 'improved',
                text: 'La quête du camp des maraudeurs indique où trouver les camps de la Prairie.',
                en: 'The marauders’ camp quest shows where to find the Meadow’s camps.',
            },
            {
                kind: 'improved',
                text: 'Les quêtes des boss et des élites légendaires n’exigent plus de venir en groupe : elles conseillent de bien se préparer, et d’emmener des compagnons si l’on en trouve.',
                en: 'Boss and legendary elite quests no longer require a party: they advise you to prepare well, and to bring companions if you can find some.',
            },
        ],
    },
    {
        version: '0.16.1',
        date: '2026-09-29',
        changes: [
            {
                kind: 'new',
                text: 'Un « ! » rouge jaillit au-dessus d’une créature qui vous prend pour cible : on voit tout de suite qui vient de vous repérer.',
                en: 'A red “!” pops up above a creature that targets you: you can see right away who just spotted you.',
            },
            {
                kind: 'improved',
                text: 'L’e-mail de changement de mot de passe porte les couleurs du jeu : la vallée et le logo en tête, le bouton doré de l’interface.',
                en: 'The password-change e-mail wears the game’s colors: the valley and the logo up top, the interface’s golden button.',
            },
            {
                kind: 'fixed',
                text: 'Les musiques ne claquent plus à chaque reprise de leur boucle, et les effets sonores sont rééquilibrés : discussion, erreurs et pièces d’or plus discrets, sorts au même niveau les uns que les autres, montée de niveau la plus sonore. Un limiteur empêche la saturation quand plusieurs sons se superposent.',
                en: 'Music no longer clicks each time its loop restarts, and sound effects are rebalanced: chat, errors and gold coins are quieter, spells are all at the same level, leveling up is the loudest. A limiter prevents clipping when several sounds overlap.',
            },
            {
                kind: 'fixed',
                text: 'Recharger la page juste après être mort (ou après avoir marché un moment) ne ramène plus le personnage là où il était quelques minutes plus tôt : il reprend exactement où il se tenait.',
                en: 'Reloading the page right after dying (or after walking for a while) no longer puts your character back where they were a few minutes earlier: they pick up exactly where they stood.',
            },
        ],
    },
    {
        version: '0.16.0',
        date: '2026-09-29',
        changes: [
            {
                kind: 'new',
                text: 'Mot de passe oublié ? Le lien sous « Se connecter » t’envoie un e-mail pour en choisir un nouveau, valable une heure. Un compte ouvert avec Google peut aussi s’en donner un.',
                en: 'Forgot your password? The link under “Log in” sends you an e-mail to choose a new one, valid for one hour. An account opened with Google can also give itself one.',
            },
        ],
    },
    {
        version: '0.15.1',
        date: '2026-09-28',
        changes: [
            {
                kind: 'improved',
                text: 'Shefira est aussi sur Instagram : le lien est sur l’accueil, sous l’encart Discord.',
                en: 'Shefira is on Instagram too: the link is on the Home screen, under the Discord box.',
            },
        ],
    },
    {
        version: '0.15.0',
        date: '2026-09-28',
        changes: [
            {
                kind: 'new',
                text: 'Shefira a son serveur Discord : rejoins-le depuis le bouton Discord en haut de l’écran en jeu, ou depuis l’encart Discord de l’accueil. Annonces, nouveautés, recherche de groupe, bugs et suggestions, tout y est.',
                en: 'Shefira has its own Discord server: join it from the Discord button at the top of the screen in game, or from the Discord box on the Home screen. Announcements, news, looking for group, bugs and suggestions, it’s all there.',
            },
        ],
    },
    {
        version: '0.14.0',
        date: '2026-09-28',
        title: 'Ils tirent de loin',
        titleEn: 'They Shoot from Afar',
        changes: [
            {
                kind: 'new',
                text: 'Des créatures attaquent désormais à distance : l’Archer gobelin dans la Forêt des Murmures, le Feu follet dans le Marais Brumeux, le Cobra cracheur dans le Désert de Braise et la Harpie des neiges dans les Pics Gelés. Elles restent à distance et tirent dès qu’elles te voient ; derrière un arbre ou un rocher, elles ne t’atteignent pas.',
                en: 'Some creatures now attack from a distance: the Goblin Archer in the Whispering Forest, the Will-o’-Wisp in the Misty Marsh, the Spitting Cobra in the Ember Desert and the Snow Harpy in the Frozen Peaks. They keep their distance and shoot as soon as they see you; behind a tree or a rock, they can’t reach you.',
            },
            {
                kind: 'new',
                text: 'Au contact, un tireur ne vaut plus grand-chose : fonce-lui dessus. Chacun a son matériau, et un succès de chasse de plus par région.',
                en: 'Up close, a shooter isn’t worth much: charge right at it. Each has its own material, and one more hunting achievement per region.',
            },
        ],
    },
    {
        version: '0.13.1',
        date: '2026-09-28',
        changes: [
            {
                kind: 'fixed',
                text: 'La connexion avec Google pouvait échouer au premier essai avec « Action refusée » : elle passe désormais, et si Google ou Steam ne répond vraiment pas, un message le dit.',
                en: 'Logging in with Google could fail on the first try with “Action refused”: it now goes through, and if Google or Steam really doesn’t answer, a message says so.',
            },
        ],
    },
    {
        version: '0.13.0',
        date: '2026-09-28',
        changes: [
            {
                kind: 'new',
                text: 'Connexion avec Google ou Steam depuis l’accueil : ton compte et tes personnages te suivent sur n’importe quel navigateur, sans mot de passe à retenir.',
                en: 'Log in with Google or Steam from the Home screen: your account and characters follow you to any browser, with no password to remember.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Déjà un compte ? Lie Google ou Steam depuis l’onglet Compte du profil : rien ne change, tu peux ensuite te connecter par ce service. Un compte invité lié devient un vrai compte et peut passer par la boutique.',
                en: 'Already have an account? Link Google or Steam from the Account tab of your profile: nothing changes, and you can then log in through that service. A linked guest account becomes a real account and can use the Shop.',
            },
            {
                kind: 'improved',
                text: 'L’accueil tient en entier sur l’écran d’un portable ou d’un téléphone, sans défiler.',
                en: 'The Home screen fits entirely on a laptop or phone screen, with no scrolling.',
            },
        ],
    },
    {
        version: '0.12.0',
        date: '2026-09-28',
        changes: [
            {
                kind: 'new',
                text: 'Une nouvelle place d’apparence, les ailes : elles se portent dans le dos, sous le personnage sur la face Apparence de l’inventaire, et les Ailes de glace battent doucement même à l’arrêt.',
                en: 'A new Appearance slot, wings: they’re worn on the back, under the character on the Appearance side of the inventory, and the Ice Wings flap gently even when standing still.',
            },
            {
                kind: 'new',
                text: 'Les Ailes de glace ne s’achètent pas : le Géant de Givre, au Trône de Givre, les laisse très rarement à ceux qui le terrassent.',
                en: 'The Ice Wings can’t be bought: the Frost Giant, on the Frost Throne, very rarely leaves them to those who bring him down.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Des ailes aux couleurs des packs, en boutique : les Ailes de flamme rejoignent le pack fondateur (obsidienne fendue de lave, pointes qui brûlent) et les Ailes du dragon le pack du Dragon (cramoisies, liserées d’or), au même prix de pack. Elles se vendent aussi à l’unité.',
                en: 'Wings in the packs’ colors, in the Shop: the Flame Wings join the Founder Pack (lava-veined obsidian, burning tips) and the Dragon Wings join the Dragon Pack (crimson, trimmed with gold), at the same pack price. They’re also sold on their own.',
            },
            {
                kind: 'improved',
                webOnly: true,
                text: 'Le bouton de la boutique, en haut de l’écran, montre maintenant un sac de courses : on voit tout de suite ce qu’il ouvre.',
                en: 'The Shop button at the top of the screen now shows a shopping bag: you can see right away what it opens.',
            },
            {
                kind: 'fixed',
                text: 'Dans l’inventaire, les objets portés gardent leurs vraies couleurs : seul le cadre des cases d’apparence est teinté de violet, plus l’image de la pièce.',
                en: 'In the inventory, worn items keep their true colors: only the frame of the Appearance slots is tinted purple, no longer the picture of the piece.',
            },
            {
                kind: 'improved',
                webOnly: true,
                text: 'L’infobulle d’une monture dit maintenant de combien elle accélère ta marche, et la boutique l’affiche sur la carte de chaque monture : le Pégase, la plus rapide du jeu, t’emmène 250 % plus vite. Il coûte désormais 19,99 €.',
                en: 'A mount’s tooltip now says how much it speeds up your walk, and the Shop shows it on each mount’s card: the Pegasus, the fastest in the game, carries you 250% faster. It now costs €19.99.',
            },
        ],
    },
    {
        version: '0.11.1',
        date: '2026-09-28',
        changes: [
            {
                kind: 'improved',
                text: 'Si personne n’est au village ou à ses portes à l’heure de l’invasion, elle est annulée et tout le serveur en est prévenu : les gobelins ne pillent plus un village vide.',
                en: 'If nobody is in the village or at its gates when the invasion is due, it’s canceled and the whole server is told: the goblins no longer loot an empty village.',
            },
            {
                kind: 'improved',
                text: 'Une pastille dorée sur le bouton du journal de quêtes (ou du menu, sur téléphone) compte les contrats remplis qui attendent d’être encaissés.',
                en: 'A golden badge on the Quest Journal button (or the menu, on phone) counts the completed contracts waiting to be claimed.',
            },
            {
                kind: 'fixed',
                text: 'Les bannières de quête, de contrat et de succès ont de nouveau un fond sombre qui remplit tout leur cadre doré.',
                en: 'Quest, contract and achievement banners once again have a dark background that fills their whole golden frame.',
            },
        ],
    },
    {
        version: '0.11.0',
        date: '2026-09-28',
        title: 'Contrats et série de connexions',
        titleEn: 'Contracts and Login Streak',
        changes: [
            {
                kind: 'new',
                text: 'Contrats : chaque jour à 6 h, trois petites tâches à ta mesure (une chasse, un camp, le forgeron, du butin, des sorts), et chaque lundi un contrat de la semaine plus gros. Ils rapportent de l’or et des matériaux d’amélioration de la région. Tout est dans le journal de quêtes (Q), onglet Contrats.',
                en: 'Contracts: every day at 6 a.m., three small tasks sized for you (a hunt, a camp, the blacksmith, loot, spells), and every Monday a bigger weekly contract. They pay gold and the region’s upgrade materials. It’s all in the Quest Journal (Q), Contracts tab.',
            },
            {
                kind: 'new',
                text: 'Premier boss du jour : la première fois que tu abats un boss de zone dans la journée, un second butin tombe pour toi. Sa barre de vie t’indique quand le bonus est encore à prendre.',
                en: 'First boss of the day: the first time you take down a zone boss each day, a second batch of loot drops for you. Its health bar tells you when the bonus is still up for grabs.',
            },
            {
                kind: 'new',
                text: 'Série de connexions : chaque jour où tu remplis un contrat compte, sans remise à zéro si tu manques un jour. Petite récompense chaque jour, et au 7e jour le Renard de lumière, un familier exclusif qui ne se vend ni ne se trouve nulle part : l’onglet Contrats te montre où tu en es.',
                en: 'Login streak: every day you complete a contract counts, with no reset if you miss a day. A small reward every day, and on day 7 the Light Fox, an exclusive pet that is neither sold nor found anywhere else: the Contracts tab shows how far along you are.',
            },
            {
                kind: 'improved',
                text: 'Nouveau logo : le portail des villages entre l’épée et le bouclier de givre, derrière le nom en lettres d’or.',
                en: 'New logo: the village portal between the sword and the frost shield, behind the name in golden letters.',
            },
            {
                kind: 'improved',
                text: 'Les portails des villages apparaissent sur la mini-carte et sur la carte du monde (M), avec leur arche.',
                en: 'Village portals appear on the minimap and the world map (M), with their arch.',
            },
            {
                kind: 'fixed',
                text: 'Le familier ne passe plus sous l’aura de ton équipement amélioré quand tu marches.',
                en: 'Your pet no longer slips under the aura of your upgraded equipment when you walk.',
            },
        ],
    },
    {
        version: '0.10.0',
        date: '2026-09-28',
        title: 'Les invasions gobelines',
        titleEn: 'The Goblin Invasions',
        changes: [
            {
                kind: 'new',
                text: 'Événements mondiaux : toutes les deux heures, à heure pile, des gobelins envahissent un village (Havrebourg, Sylvebois, Brumevase, Sablerouge puis Givrecime, à tour de rôle), poussant devant eux les créatures de la région. L’alerte sonne pour tout le serveur cinq minutes avant : repousse les quatre vagues et abats leur chef de guerre avant la fin du temps pour sauver le village. Plus il y a de défenseurs, plus les vagues sont nombreuses et chaque assaillant coriace.',
                en: 'World events: every two hours, on the hour, goblins invade a village (Havrebourg, Sylvebois, Brumevase, Sablerouge then Givrecime, in turn), driving the region’s creatures before them. The alarm sounds for the whole server five minutes before: push back the four waves and take down their warlord before time runs out to save the village. The more defenders there are, the more numerous the waves and the tougher each attacker.',
            },
            {
                kind: 'new',
                text: 'Pendant l’attaque, un classement en direct montre les cinq meilleurs défenseurs avec leur portrait, ta place et ta part des points : coups portés, coups encaissés et gobelins achevés comptent. Si tu tombes pendant l’invasion, tes points restent acquis mais tu ne peux plus aider. Atteins au moins le quart de la part moyenne pour être récompensé (XP, matériaux rares et une pièce de la région, à tes pieds) ; les trois premiers reçoivent un meilleur butin et la relique de la région.',
                en: 'During the attack, a live leaderboard shows the top five defenders with their portraits, your rank and your share of the points: hits dealt, hits taken and goblins finished off all count. If you fall during the invasion, your points are kept but you can no longer help. Reach at least a quarter of the average share to be rewarded (XP, rare materials and a piece from the region, at your feet); the top three get better loot and the region’s relic.',
            },
            {
                kind: 'new',
                text: 'La carte du monde (M) liste les prochains événements et marque le village attaqué, comme la mini-carte. Tant que dure l’invasion, marchand, forgeron, fondeur, alchimiste et hôtel des ventes du village ne servent plus personne ; le portail reste ouvert pour venir le défendre.',
                en: 'The world map (M) lists upcoming events and marks the village under attack, like the minimap. While the invasion lasts, the village’s merchant, blacksmith, smelter, alchemist and Auction House serve no one; the portal stays open so you can come and defend it.',
            },
        ],
    },
    {
        version: '0.9.0',
        date: '2026-09-28',
        title: 'Des coups qui se sentent',
        titleEn: 'Hits You Can Feel',
        changes: [
            {
                kind: 'new',
                text: 'Coups critiques : une fois sur dix, ton coup ou ton sort frappe moitié plus fort, avec un gros chiffre doré, un son à lui, un court arrêt sur image et une secousse de l’écran.',
                en: 'Critical hits: one time in ten, your attack or spell hits half again as hard, with a big golden number, its own sound, a short freeze-frame and a screen shake.',
            },
            {
                kind: 'new',
                text: 'Un objet rare, épique ou légendaire tombe dans une colonne de lumière de sa couleur, visible de loin, avec un son à lui.',
                en: 'A rare, epic or legendary item drops in a pillar of light of its color, visible from afar, with its own sound.',
            },
            {
                kind: 'improved',
                text: 'L’écran tremble sous un sort de zone, une charge ou un coup qui t’arrache une bonne part de ta vie, et s’illumine quand tu gagnes un niveau.',
                en: 'The screen shakes under an area spell, a charge or a hit that takes a big chunk of your health, and lights up when you level up.',
            },
            {
                kind: 'new',
                text: 'Ta guilde est prévenue quand un de ses membres gagne un niveau.',
                en: 'Your guild is told when one of its members levels up.',
            },
            {
                kind: 'new',
                text: 'Réglages : « Secousses et éclairs de l’écran » les coupe si tu préfères un écran calme.',
                en: 'Settings: “Screen shakes and flashes” turns them off if you prefer a calm screen.',
            },
        ],
    },
    {
        version: '0.8.1',
        date: '2026-09-28',
        changes: [
            {
                kind: 'fixed',
                text: 'Près d’un boss, ses effets (saignement, ralenti) restent visibles sous sa barre de vie même quand tu ne le cibles plus, et le cadre d’un renfort ciblé se place sous la barre du boss au lieu de la recouvrir.',
                en: 'Near a boss, its effects (bleeding, slowed) stay visible under its health bar even when you no longer target it, and the frame of a targeted reinforcement sits under the boss bar instead of covering it.',
            },
        ],
    },
    {
        version: '0.8.0',
        date: '2026-09-28',
        changes: [
            {
                kind: 'new',
                text: 'La créature que tu combats ou que tu vises s’affiche en haut de l’écran, avec son niveau, sa vie et les effets qu’elle subit (étourdie, ralentie, saignement, provoquée) et le temps qu’il leur reste.',
                en: 'The creature you’re fighting or aiming at shows at the top of the screen, with its level, its health and the effects on it (stunned, slowed, bleeding, taunted) and the time they have left.',
            },
        ],
    },
    {
        version: '0.7.0',
        date: '2026-09-28',
        title: 'Les quêtes des Cinq Terres',
        titleEn: 'Quests of the Five Lands',
        changes: [
            {
                kind: 'new',
                text: 'Le Guerrier a désormais un grimoire de douze sorts, appris au fil des régions : Charge, Entaille, Mur de bouclier, Coup de bouclier, Provocation, Onde sismique, Rage, Exécution et, au niveau 20, la Colère du titan. Chaque région apporte les siens, et le sort phare de la Forêt, du Marais, du Désert et des Pics s’apprend en chemin dans sa chaîne de quêtes.',
                en: 'The Warrior now has a Spellbook of twelve spells, learned region by region: Charge, Slash, Shield Wall, Shield Bash, Taunt, Seismic Wave, Rage, Execute and, at level 20, Titan’s Wrath. Each region brings its own, and the signature spell of the Forest, the Marsh, the Desert and the Peaks is learned along its quest chain.',
            },
            {
                kind: 'new',
                text: 'Tu choisis les quatre sorts de ta barre dans le Grimoire (touche K ou le livre du HUD), hors combat : un sort appris se pose tout seul sur une case libre.',
                en: 'You choose the four spells on your bar in the Spellbook (K key or the book on the HUD), outside combat: a newly learned spell drops into a free slot by itself.',
            },
            {
                kind: 'new',
                text: 'Les effets qui te portent (Rage, Mur de bouclier) s’affichent au-dessus de ta barre de sorts avec le temps qu’il leur reste ; survole-les pour voir ce qu’ils font.',
                en: 'The effects on you (Rage, Shield Wall) show above your spell bar with the time they have left; hover them to see what they do.',
            },
            {
                kind: 'new',
                text: 'Les sorts laissent des effets : une créature étourdie ne frappe plus, ralentie elle traîne, blessée elle saigne, provoquée elle ne s’en prend qu’à toi. Les boss ne se laissent ni étourdir ni provoquer, mais un Coup de bouclier bien placé retarde leur attaque annoncée.',
                en: 'Spells leave effects: a stunned creature stops hitting, a slowed one drags along, a wounded one bleeds, a taunted one only goes after you. Bosses can be neither stunned nor taunted, but a well-timed Shield Bash delays their announced attack.',
            },
            {
                kind: 'improved',
                text: 'Le Cri de guerre s’apprend au niveau 2 et le Tourbillon au niveau 3, pour découvrir les sorts un par un.',
                en: 'War Cry is learned at level 2 and Whirlwind at level 3, so you discover spells one at a time.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Un personnage se supprime depuis la liste des personnages ou l’onglet Compte du profil : le bouton « Supprimer » demande de retaper son nom. Le personnage en jeu et celui qui a reçu des achats de la boutique ne peuvent pas l’être.',
                en: 'A character can be deleted from the character list or the Account tab of the profile: the “Delete” button asks you to retype its name. The character in play, and one that has received Shop purchases, can’t be deleted.',
            },
            {
                kind: 'new',
                text: 'Les quêtes arrivent : le Capitaine Aldric, sur la place de Havrebourg, ouvre une chaîne de quinze quêtes : on commence par toucher l’arche bleue de la place pour enregistrer le portail, puis la chaîne mène de la forge et de l’étal du marchand, où l’on apprend à acheter son équipement, à la Tour de Guet, au Cercle des Anciens, à un camp et jusqu’au Grand Épouvantail, puis sur la route de Sylvebois.',
                en: 'Quests are here: Captain Aldric, on Havrebourg’s square, opens a chain of fifteen quests: you start by touching the blue arch on the square to register the portal, then the chain takes you from the forge and the merchant’s stall, where you learn to buy your equipment, to the Watchtower, the Circle of Elders, a camp and on to the Great Scarecrow, then onto the road to Sylvebois.',
            },
            {
                kind: 'new',
                text: 'Un « ! » doré au-dessus d’un habitant propose une quête, un « ? » doré attend ton retour. Le journal de quêtes (touche Q) et le suivi à droite de l’écran disent ce qu’il reste à faire ; la mini-carte et la carte du monde montrent où aller.',
                en: 'A golden “!” above a villager offers a quest, a golden “?” awaits your return. The Quest Journal (Q key) and the tracker on the right of the screen say what’s left to do; the minimap and the world map show where to go.',
            },
            {
                kind: 'new',
                text: 'En groupe, les créatures abattues ensemble comptent pour chaque membre qui a la même quête.',
                en: 'In a party, creatures defeated together count for every member who has the same quest.',
            },
            {
                kind: 'new',
                text: 'Un petit « ! » doré devant le nom d’une créature dit qu’une de tes quêtes la demande, à abattre ou pour ce qu’elle laisse. Sur la mini-carte comme sur la carte du monde, le « ! » d’un habitant ne s’affiche que s’il a une quête pour toi, et son « ? » quand il t’attend.',
                en: 'A small golden “!” in front of a creature’s name means one of your quests wants it, to defeat or for what it drops. On the minimap as on the world map, a villager’s “!” only shows if they have a quest for you, and their “?” when they’re waiting for you.',
            },
            {
                kind: 'improved',
                text: 'Les rats, renards et sangliers de la Prairie rapportent un peu moins d’expérience, et le cuir de sanglier un peu moins d’or : les quêtes paient le reste.',
                en: 'Meadow rats, foxes and boars give a little less experience, and boar leather a little less gold: quests pay the rest.',
            },
            {
                kind: 'new',
                text: 'La Guetteuse te confie un appât : pose-le près d’un terrier de rats (un cercle doré et une flèche te montrent le plus proche) pour faire sortir le Rat pesteux, pour toi et ton groupe.',
                en: 'The Lookout hands you a bait: set it near a rat burrow (a golden circle and an arrow show you the nearest one) to lure out the Plague Rat, for you and your party.',
            },
            {
                kind: 'improved',
                text: 'Les créatures rares, épiques et légendaires sont nettement plus coriaces et frappent plus fort.',
                en: 'Rare, epic and legendary creatures are much tougher and hit harder.',
            },
            {
                kind: 'new',
                text: 'Chaque région a sa chaîne de quêtes, de plus en plus longue : la Forêt des Murmures (18 quêtes, les bûcherons et la forêt qui brûle), le Marais Brumeux (22 quêtes, les tombes ouvertes de Brumevase), le Désert de Braise (24 quêtes, la caravane disparue et le masque du Pharaon) et les Pics Gelés (28 quêtes, la saga du frère du Jarl), jusqu’au retour du héros à Havrebourg.',
                en: 'Every region has its quest chain, longer and longer: the Whispering Forest (18 quests, the woodcutters and the burning forest), the Misty Marsh (22 quests, the open graves of Brumevase), the Ember Desert (24 quests, the lost caravan and the Pharaoh’s mask) and the Frozen Peaks (28 quests, the saga of the Jarl’s brother), right up to the hero’s return to Havrebourg.',
            },
            {
                kind: 'new',
                text: 'Dix-neuf nouveaux habitants donnent ou attendent des quêtes, dans les villages et près des lieux de chaque région : bûcheron, druidesse, passeur, fossoyeuse, chef de caravane, prêtresse, skalde, voyante…',
                en: 'Nineteen new villagers give or await quests, in the villages and near each region’s landmarks: woodcutter, druid, ferryman, gravedigger, caravan leader, priestess, skald, seer…',
            },
            {
                kind: 'new',
                text: 'De nouveaux appâts font sortir une élite dans chaque région, pour toi et ton groupe : ver de tourbe, idole d’écailles, carcasse faisandée, encens funéraire, miel givré, corne des hurlements…',
                en: 'New baits lure out an elite in each region, for you and your party: peat worm, scale idol, rotting carcass, funeral incense, frosted honey, horn of howls…',
            },
            {
                kind: 'new',
                text: 'Les créatures légendaires ont aussi leur appât, à affronter en groupe : sifflet d’argent pour Croc-d’Argent, lotus noir pour le Roi des Tourbières, cartouche royal pour le Pharaon oublié, orbe de tempête pour le Vieux Blizzard. Les membres de ton groupe voient la créature que tu as fait sortir et peuvent la combattre avec toi.',
                en: 'Legendary creatures have their bait too, to face as a party: silver whistle for Silverfang, black lotus for the Bog King, royal cartouche for the Forgotten Pharaoh, storm orb for the Elder Blizzard. Your party members see the creature you lured out and can fight it with you.',
            },
            {
                kind: 'new',
                text: 'Des récompenses uniques, qu’on ne trouve nulle part ailleurs : la Lame de Croc-d’Argent, l’Écu du Roi des Tourbières, le Sceptre d’Ounas-Rê et, au bout du voyage, l’Épée d’Eirik.',
                en: 'Unique rewards you won’t find anywhere else: the Silverfang Blade, the Bog King’s Shield, Ounas-Rê’s Scepter and, at the end of the journey, Eirik’s Sword.',
            },
            {
                kind: 'improved',
                text: 'Les créatures de la Forêt, du Marais, du Désert et des Pics rapportent moins d’expérience : leurs quêtes paient le reste, et une région prend un peu plus de temps qu’avant à qui suit sa chaîne.',
                en: 'Forest, Marsh, Desert and Peaks creatures give less experience: their quests pay the rest, and a region takes a little longer than before for those who follow its chain.',
            },
            {
                kind: 'new',
                text: 'Les Écuries de Havrebourg ouvrent à la sortie de la ville, sur la route de Sylvebois, avec leur enclos et leurs chevaux : à la fin de la Prairie, le Palefrenier Gaspard t’y remet ton propre cheval.',
                en: 'The Havrebourg Stables open on the edge of town, on the road to Sylvebois, with their paddock and horses: at the end of the Meadow, Stablemaster Gaspard hands you your very own horse.',
            },
            {
                kind: 'new',
                text: 'Un bouton à côté des sorts permet de monter en selle et d’en descendre (touche W).',
                en: 'A button next to your spells lets you get in and out of the saddle (W key).',
            },
            {
                kind: 'new',
                text: 'Au nord de Sablerouge, sur la route de Havrebourg, le relais des chameliers et ses chameaux en liberté : aide le Chamelier Idris à retrouver les bêtes de la caravane de Kassim, et il te donnera le Chameau de Sablerouge, un peu plus rapide que le cheval de Havrebourg.',
                en: 'North of Sablerouge, on the road to Havrebourg, the camel drivers’ post and its free-roaming camels: help Camel Driver Idris find the beasts of Kassim’s caravan, and he’ll give you the Sablerouge Camel, a little faster than the Havrebourg Horse.',
            },
            {
                kind: 'new',
                text: 'La Prairie a son propre équipement, celui du berger : houlette ferrée, bouclier d’osier, chapeau de paille, pelisse, gants et guêtres, dans les quatre raretés. Le set en bois reste celui du tutoriel : les créatures de la Prairie ne le lâchent plus.',
                en: 'The Meadow has its own equipment, the shepherd’s: iron-shod crook, wicker shield, straw hat, fur coat, gloves and gaiters, in all four rarities. The wooden set stays the tutorial’s: Meadow creatures no longer drop it.',
            },
            {
                kind: 'improved',
                text: 'Chez le marchand, survoler un objet (ou le toucher sur téléphone) affiche sa fiche. Là comme dans le sac, la fiche d’une pièce d’équipement dit ce qu’elle change par rapport à celle que tu portes, améliorations comprises : DPS, Défense et Vie en plus ou en moins.',
                en: 'At the merchant, hovering over an item (or tapping it on phone) shows its details. There as in the bag, an equipment piece’s details say what it changes compared with the one you’re wearing, upgrades included: DPS, Defense and Health up or down.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Le pack fondateur donne le titre « Fondateur », et le pack du dragon le titre « Cœur de Dragon », à porter au-dessus de ton nom sur tous les personnages du compte (Profil, onglet Succès). Ce n’est pas un succès : tous les succès restent à portée sans rien acheter.',
                en: 'The Founder Pack gives the “Founder” title, and the Dragon Pack the “Dragonheart” title, to wear above your name on every character of the account (Profile, Achievements tab). It isn’t an achievement: every achievement stays within reach without buying anything.',
            },
            {
                kind: 'fixed',
                text: 'L’hôtel des ventes ne répond plus au clic bien au-dessus de son toit : seule sa façade l’ouvre.',
                en: 'The Auction House no longer responds to clicks well above its roof: only its front opens it.',
            },
            {
                kind: 'improved',
                text: 'La Lame des Cinq Terres est redessinée : une lame large aux couleurs des cinq régions, des neiges des Pics aux bois de la Forêt, sous une garde d’or ailée, parcourue d’un éclat de lumière et d’étincelles quand on la tient.',
                en: 'The Blade of the Five Lands is redrawn: a broad blade in the colors of the five regions, from the snows of the Peaks to the woods of the Forest, under a winged golden guard, shot through with a gleam of light and sparks when held.',
            },
        ],
    },
    {
        version: '0.6.0',
        date: '2026-09-28',
        changes: [
            {
                kind: 'new',
                text: 'Passer un niveau se voit : un anneau d’or s’étend au sol, une colonne de lumière monte à travers le personnage et des étincelles s’envolent autour de lui, pour soi comme pour les joueurs alentour.',
                en: 'Leveling up shows: a golden ring spreads across the ground, a pillar of light rises through the character and sparks fly around them, for you as for nearby players.',
            },
        ],
    },
    {
        version: '0.5.0',
        date: '2026-09-28',
        changes: [
            {
                kind: 'new',
                text: 'Chez le forgeron, l’alchimiste et le fondeur, survoler un matériau (ou le toucher sur téléphone) montre où l’obtenir : les créatures qui le laissent et leur chance, les élites, le boss, les coffres de sa région.',
                en: 'At the blacksmith, the alchemist and the smelter, hovering over a material (or tapping it on phone) shows where to get it: the creatures that drop it and their chance, the elites, the boss, the chests of its region.',
            },
        ],
    },
    {
        version: '0.4.0',
        date: '2026-09-28',
        changes: [
            {
                kind: 'new',
                text: 'L’infobulle d’un objet dit à quoi il sert : les potions de l’alchimiste qu’il entre dans, les améliorations du forgeron qu’il permet, ce qu’en fait le fondeur, ou qu’il ne sert qu’à être vendu.',
                en: 'An item’s tooltip says what it’s for: the alchemist’s potions it goes into, the blacksmith upgrades it allows, what the smelter makes of it, or that it’s only good for selling.',
            },
        ],
    },
    {
        version: '0.3.0',
        date: '2026-09-27',
        title: 'Succès et titres',
        titleEn: 'Achievements and Titles',
        changes: [
            {
                kind: 'new',
                text: 'Succès : chaque région a les siens (chasser ses créatures, vaincre ses élites et son boss, activer son portail, ouvrir un coffre caché, nettoyer un camp), à suivre dans l’onglet Succès du profil ou avec la touche J.',
                en: 'Achievements: each region has its own (hunt its creatures, defeat its elites and its boss, activate its portal, open a hidden chest, clear a camp), to follow in the Achievements tab of the profile or with the J key.',
            },
            {
                kind: 'new',
                text: 'Titres : une région terminée et quelques exploits donnent un titre, porté au-dessus du nom et choisi dans l’onglet Succès.',
                en: 'Titles: a finished region and a few feats give a title, worn above your name and chosen in the Achievements tab.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'Terminer les cinq régions donne le titre « Conquérant des Cinq Terres » et la Lame des Cinq Terres, une apparence d’arme qu’aucune boutique ne vend.',
                en: 'Finishing all five regions gives the title “Conqueror of the Five Lands” and the Blade of the Five Lands, a weapon appearance no shop sells.',
            },
        ],
    },
    {
        version: '0.2.1',
        date: '2026-09-27',
        changes: [
            {
                kind: 'fixed',
                text: 'Le personnage ne recule plus en pleine marche : le serveur ne prend plus de retard quand beaucoup de créatures se déplacent autour.',
                en: 'Your character no longer jumps back mid-walk: the server no longer falls behind when lots of creatures are moving around.',
            },
        ],
    },
    {
        version: '0.2.0',
        date: '2026-09-27',
        changes: [
            {
                kind: 'new',
                text: 'Sept nouvelles créatures : renard, sylvain, caïman, scorpion géant, salamandre de braise, loup des glaces et revenant du givre, chacune avec son matériau.',
                en: 'Seven new creatures: fox, treant, caiman, giant scorpion, ember salamander, ice wolf and frost revenant, each with its own material.',
            },
            {
                kind: 'improved',
                text: 'Le personnage part dès le clic, sans attendre la réponse du serveur, et marche d’un pas régulier.',
                en: 'Your character sets off as soon as you click, without waiting for the server, and walks at a steady pace.',
            },
            {
                kind: 'improved',
                webOnly: true,
                text: 'Boutique : la renonciation au droit de rétractation se coche avant de payer, et un compte avec e-mail est demandé pour acheter.',
                en: 'Shop: the waiver of the right of withdrawal is ticked before paying, and an account with an e-mail is required to buy.',
            },
            {
                kind: 'improved',
                webOnly: true,
                text: 'Un objet acheté s’échange et se vend à l’hôtel des ventes quatorze jours après sa livraison.',
                en: 'A purchased item can be traded and sold at the Auction House fourteen days after delivery.',
            },
            {
                kind: 'new',
                text: 'Les conditions générales de vente et les mentions légales, depuis l’accueil.',
                en: 'The terms of sale and the legal notice, from the Home screen.',
            },
            {
                kind: 'fixed',
                text: 'Les réglages se sauvegardent bien sur le compte.',
                en: 'Settings are now properly saved to the account.',
            },
            {
                kind: 'fixed',
                webOnly: true,
                text: 'Un achat reçu avec le sac plein attend dans la boutique, à récupérer, au lieu d’être annoncé comme livré.',
                en: 'A purchase received with a full bag waits in the Shop, ready to collect, instead of being announced as delivered.',
            },
            {
                kind: 'fixed',
                text: 'Le tag de guilde se place après le nom dans le chat, et l’invitation de groupe ne passe plus sous la croix.',
                en: 'The guild tag comes after the name in chat, and the party invitation no longer slips under the close button.',
            },
            {
                kind: 'fixed',
                text: 'L’armure du torse ne disparaît plus pendant l’attaque de profil.',
                en: 'Chest armor no longer disappears during the side-view attack.',
            },
        ],
    },
    {
        version: '0.1.1',
        date: '2026-09-27',
        changes: [
            {
                kind: 'new',
                webOnly: true,
                text: 'La mention bêta et le numéro de version sur l’écran d’accueil.',
                en: 'The beta badge and the version number on the Home screen.',
            },
            {
                kind: 'new',
                text: 'Le journal des nouveautés, ouvert depuis l’accueil.',
                en: 'The changelog, opened from the Home screen.',
            },
            {
                kind: 'new',
                text: 'Le son se coupe dès l’écran d’accueil, par le haut-parleur en haut à droite.',
                en: 'Sound can be muted right from the Home screen, with the speaker at the top right.',
            },
            {
                kind: 'improved',
                text: 'Retour à l’accueil et déconnexion passent dans les réglages ; le bouton maison quitte la barre du haut.',
                en: 'Back to Home and Log out move into the settings; the house button leaves the top bar.',
            },
        ],
    },
    {
        version: '0.1.0',
        date: '2026-09-27',
        title: 'Ouverture de la bêta',
        webOnlyTitle: true,
        titleEn: 'Beta Launch',
        changes: [
            {
                kind: 'new',
                text: 'Un monde ouvert de cinq biomes autour de Havrebourg, avec leurs villages, grottes, camps et coffres cachés.',
                en: 'An open world of five biomes around Havrebourg, with their villages, caves, camps and hidden chests.',
            },
            {
                kind: 'new',
                text: 'Un boss par biome, seul au fond de son donjon.',
                en: 'One boss per biome, alone at the heart of its dungeon.',
            },
            {
                kind: 'new',
                text: 'Groupes, amis, guildes, échange entre joueurs et hôtel des ventes.',
                en: 'Parties, friends, guilds, player trading and the Auction House.',
            },
            {
                kind: 'new',
                text: 'Forgeron, fondeur, alchimiste et marchand dans chaque village.',
                en: 'Blacksmith, smelter, alchemist and merchant in every village.',
            },
            {
                kind: 'new',
                webOnly: true,
                text: 'La boutique de cosmétiques et de compagnons.',
                en: 'The Shop for cosmetics and companions.',
            },
            {
                kind: 'new',
                text: 'Le jeu se joue au téléphone, en paysage.',
                en: 'The game plays on phones, in landscape.',
            },
            {
                kind: 'improved',
                text: 'Un indicateur de ping, à activer dans les réglages d’affichage.',
                en: 'A ping indicator, to turn on in the Display settings.',
            },
        ],
    },
];
/** The game's version: the newest entry's. */
export const GAME_VERSION = CHANGELOG[0]?.version ?? '0.0.0';
//# sourceMappingURL=changelog.js.map